import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Archive,
  Building2,
  ClipboardCheck,
  Download,
  Link2,
  ListChecks,
  Lock,
  Mail,
  MapPin,
  Pencil,
  Play,
  Printer,
  RotateCcw,
  Save,
  Search,
  Send,
  Phone,
  Paperclip,
  Unlock,
  Upload,
  X as XIcon,
} from 'lucide-react';
import { machVertragsHtmlEigenstaendig, NUR_ONLINE_HINWEIS } from '../lib/eigenstaendigesHtml';

const STANDARD_DOCUMENTS = [
  {
    key: 'standarddoc-grundbuchauszug',
    label: 'Anschreiben Grundbuchauszug',
    description: 'Beantragung eines Grundbuchauszugs für das Projektgrundstück.',
    file: 'Anschreiben_Grundbuchauszug_ENPOWER_CI.html',
  },
  {
    key: 'standarddoc-bauunterlagen',
    label: 'Anschreiben Bauunterlagen',
    description: 'Anforderung statischer Berechnungen und Tragwerksnachweise zum Gebäude.',
    file: 'Anschreiben_Bauunterlagen_ENPOWER_CI.html',
  },
  {
    key: 'standarddoc-grundbucheintragung',
    label: 'Anschreiben Grundbucheintragung',
    description: 'Anschreiben für die weitere Bearbeitung der Grundbucheintragung.',
    file: 'Anschreiben_Grundbucheintragung_ENPOWER_CI.html',
  },
  {
    // Seit 29.10.2026 (GENEHMIGUNGSWEGE-PLAN.md im Lageplan-Repo, GW1.4). Die Werte aus dem
    // „Genehmigung Datensatz“ des Lageplan-Tools setzt der Server ein (genehmigungsanfrage.js).
    key: 'standarddoc-verfahrensfreiheit-st',
    label: 'Anfrage Verfahrensfreiheit (Sachsen-Anhalt)',
    description: 'Bitte an die untere Bauaufsicht, die Verfahrensfreiheit eines Storion-Speichers zu bestätigen.',
    file: 'Anfrage_Verfahrensfreiheit_ST_ENPOWER_CI.html',
  },
  {
    // Seit 30.10.2026 (Nutzer: „Zusätzlich fehlen Bayern und Baden-Württemberg.“), gleiche Platzhalter.
    key: 'standarddoc-verfahrensfreiheit-by',
    label: 'Anfrage Verfahrensfreiheit (Bayern)',
    description: 'Bitte an die untere Bauaufsicht (Landratsamt oder kreisfreie Stadt), die Verfahrensfreiheit eines Storion-Speichers nach Art. 57 BayBO zu bestätigen.',
    file: 'Anfrage_Verfahrensfreiheit_BY_ENPOWER_CI.html',
  },
  {
    key: 'standarddoc-verfahrensfreiheit-bw',
    label: 'Anfrage Verfahrensfreiheit (Baden-Württemberg)',
    description: 'Bitte an die untere Baurechtsbehörde, die Verfahrensfreiheit eines Storion-Speichers nach § 50 LBO zu bestätigen.',
    file: 'Anfrage_Verfahrensfreiheit_BW_ENPOWER_CI.html',
  },
  {
    // Seit 31.10.2026 (Nutzer: „ich möchte nun für Niedersachsen eine Vorlage haben“), gleiche Platzhalter wie ST.
    key: 'standarddoc-verfahrensfreiheit-ni',
    label: 'Anfrage Verfahrensfreiheit (Niedersachsen)',
    description: 'Bitte an die untere Bauaufsicht (Landkreis, Region Hannover oder Stadt), die Verfahrensfreiheit eines Storion-Speichers nach § 60 NBauO mit Anhang Nr. 3.8 zu bestätigen.',
    file: 'Anfrage_Verfahrensfreiheit_NI_ENPOWER_CI.html',
  },
  // Seit 31.10.2026 (Nutzer: Länder, „wo es für Kleinspeicher besonders gut ist“), gleiche Platzhalter;
  // Schleswig-Holstein und Saarland messen den Rauminhalt statt der Grundfläche.
  {
    key: 'standarddoc-verfahrensfreiheit-nrw',
    label: 'Anfrage Verfahrensfreiheit (Nordrhein-Westfalen)',
    description: 'Bitte an die untere Bauaufsicht (Kreis, Städteregion Aachen oder Stadt), die Verfahrensfreiheit eines Storion-Speichers nach § 62 BauO NRW zu bestätigen.',
    file: 'Anfrage_Verfahrensfreiheit_NRW_ENPOWER_CI.html',
  },
  {
    key: 'standarddoc-verfahrensfreiheit-sl',
    label: 'Anfrage Verfahrensfreiheit (Saarland)',
    description: 'Bitte an die untere Bauaufsicht (Landkreis, Regionalverband Saarbrücken oder Stadt), die Verfahrensfreiheit eines Storion-Speichers nach § 61 LBO zu bestätigen.',
    file: 'Anfrage_Verfahrensfreiheit_SL_ENPOWER_CI.html',
  },
  {
    key: 'standarddoc-verfahrensfreiheit-sh',
    label: 'Anfrage Verfahrensfreiheit (Schleswig-Holstein)',
    description: 'Bitte an die untere Bauaufsicht (Kreis oder Stadt), die Verfahrensfreiheit eines Storion-Speichers nach § 61 LBO zu bestätigen.',
    file: 'Anfrage_Verfahrensfreiheit_SH_ENPOWER_CI.html',
  },
  {
    key: 'standarddoc-verfahrensfreiheit-th',
    label: 'Anfrage Verfahrensfreiheit (Thüringen)',
    description: 'Bitte an die untere Bauaufsicht (Landratsamt oder Stadt), die Verfahrensfreiheit eines Storion-Speichers nach § 63 ThürBO zu bestätigen.',
    file: 'Anfrage_Verfahrensfreiheit_TH_ENPOWER_CI.html',
  },
];

// Anfragen Verfahrensfreiheit je Bundesland: gleicher Betreff, gleicher Begleittext.
const istVerfahrensfreiheit = (key) => String(key || '').startsWith('standarddoc-verfahrensfreiheit-');

const STANDARD_DOCUMENTS_URL = process.env.REACT_APP_CONTRACTS_AM_URL
  || 'https://gjouokpgdwlcdjdenpdt.supabase.co/functions/v1/contracts-am';

const TEMPLATE_BASE_HREF = '/standard-documents/';
const EDITOR_SCRIPT = '/contracts/contract-editor.js?v=tool-editor-20260923-mobil1';
const PAGINATION_SCRIPT = '/contracts/contract-pagination.js?v=tool-pagination-20260610-textsafe1';

const FIELD_HINTS = [
  'DEAL.TITLE',
  'DEAL.UF_CRM_1749573387243',
  'CONTACT.NAME',
  'CONTACT.LAST_NAME',
  'CONTACT.EMAIL',
  'CONTACT.PHONE',
  'COMPANY.TITLE',
  'COMPANY.ADDRESS',
  'UF_CRM_1752311841010',
  'UF_CRM_175231956769',
];

const STANDARD_DOCUMENT_MAIL_SENDERS = [
  { email: 'l.klein@en-power.eu', label: 'Lee-Ann Sophie Klein' },
  { email: 'r.kirsten@en-power.eu', label: 'Randy Wesley Kirsten' },
  { email: 'netzplanung@en-power.eu', label: 'Netzplanung' },
];

/**
 * Rueckrufnummer im Anschreiben. Bewusst EINE feste Nummer statt der
 * Durchwahl des jeweiligen Absenders: Der Empfaenger (Bauamt, Grundbuchamt)
 * soll immer dieselbe Stelle erreichen, unabhaengig davon, wer das Schreiben
 * ausgeloest hat.
 */
const BEARBEITER_TELEFON = '+49 160 8732681';

/**
 * Kontaktdaten des internen Bearbeiters fuer die Anschreiben.
 *
 * Diese Platzhalter hiessen frueher "E-Mail Kunde" und "Telefon Kunde" - das
 * war irrefuehrend: Im Text steht "Bitte uebermitteln Sie die Unterlagen an
 * ...", gemeint ist also die Adresse UNSERES Bearbeiters, an die das Amt
 * antworten soll, nicht die des Kunden. Waeren dort Kundendaten gelandet,
 * haette das Amt die Unterlagen an den Kunden geschickt.
 *
 * Die Adresse folgt immer dem im Tool gewaehlten Absender - wer die Mail
 * verschickt, bekommt auch die Antwort.
 */
function buildBearbeiterMapping(absenderEmail) {
  const email = String(absenderEmail || '').trim();
  const name = absenderName(email);
  return {
    'E-Mail Bearbeiter': email,
    'Telefon Bearbeiter': BEARBEITER_TELEFON,
    // Die Zeile unter der Unterschrift. Sie muss dieselbe Person nennen, die
    // auch unterschreibt - sonst traegt der Brief zwei verschiedene Namen.
    'Firma / Vertretungsberechtigte Person': name,
    // Alte Schreibweise weiter bedienen: In bereits gespeicherten Vorlagen und
    // in hochgeladenen HTML-Dateien stehen die Platzhalter noch unter dem
    // alten Namen. Ohne diese Zeilen blieben sie dort unersetzt stehen.
    'E-Mail Kunde': email,
    'Telefon Kunde': BEARBEITER_TELEFON,
  };
}

/**
 * Klarname zur Absenderadresse. Faellt auf den Adressteil vor dem @ zurueck,
 * damit nie ein leerer Name unter der Unterschrift steht.
 */
function absenderName(absenderEmail) {
  const email = String(absenderEmail || '').trim().toLowerCase();
  const treffer = STANDARD_DOCUMENT_MAIL_SENDERS.find(
    (eintrag) => eintrag.email.toLowerCase() === email,
  );
  if (treffer) return treffer.label;
  return email ? email.split('@')[0] : DEFAULT_STANDARD_DOCUMENT_SIGNER;
}

/** Fallback-Anrede, wenn kein Ansprechpartner bekannt ist. */
const STANDARD_ANREDE = 'Sehr geehrte Damen und Herren';

/**
 * Briefdatum im Format "17. September 2026".
 *
 * Gehoert nicht ins Feld-Mapping: Das Datum eines Anschreibens ist immer der
 * Tag, an dem es erzeugt wird - ein Bitrix-Feld dafuer gaebe es gar nicht.
 * Ohne diesen Wert blieb "{{Datum}}" im fertigen Brief stehen.
 */
function heutigesBriefdatum() {
  return new Date().toLocaleDateString('de-DE', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
}

/**
 * Platzhalter, die das Tool selbst fuellt - fuer sie gibt es kein Bitrix-Feld.
 * Im Mapping-Panel erscheinen sie deshalb nicht als leeres Eingabefeld (das
 * sah bisher aus, als haette jemand vergessen, sie zuzuordnen), sondern mit
 * dem Wert, der tatsaechlich eingesetzt wird.
 */
const AUTOMATISCHE_PLATZHALTER = {
  'E-Mail Bearbeiter': 'folgt der gewählten Absenderadresse',
  'Telefon Bearbeiter': 'feste Rückrufnummer',
  'Firma / Vertretungsberechtigte Person': 'folgt dem gewählten Absender – wie die Unterschrift',
  'E-Mail Kunde': 'alt – folgt der gewählten Absenderadresse',
  'Telefon Kunde': 'alt – feste Rückrufnummer',
  Anrede: 'immer „Sehr geehrte Damen und Herren“',
  Datum: 'Tagesdatum beim Erzeugen',
};

/** Was steht in diesem automatischen Feld gerade konkret drin? */
function automatischerWert(platzhalter, absenderEmail) {
  const bearbeiter = buildBearbeiterMapping(absenderEmail);
  if (platzhalter in bearbeiter) return bearbeiter[platzhalter];
  if (platzhalter === 'Datum') return heutigesBriefdatum();
  if (platzhalter === 'Anrede') return STANDARD_ANREDE;
  return '';
}

const GRUNDBUCHAMT_DIRECTORY = [
  {
    id: 'ag-aschersleben-grundbuchamt-stassfurt',
    label: 'Amtsgericht Aschersleben - Grundbuchamt',
    placeLabel: 'Aschersleben',
    searchTerms: ['aschersleben', '06449', '06434', '06466', '06467', 'seeland', 'gatersleben', 'stassfurt', 'staßfurt'],
    recipient: 'Amtsgericht Aschersleben - Grundbuchamt',
    addressLines: ['Theodor-Römer-Weg 3', '06449 Aschersleben'],
    street: 'Theodor-Römer-Weg 3',
    postalCity: '06449 Aschersleben',
    email: 'ag-asl@justiz.sachsen-anhalt.de',
    phone: '03473 8800',
    fax: '03473 880188',
    sourceLabel: 'Justizportal Orts- und Gerichtsverzeichnis',
    sourceUrl: 'https://www.justizadressen.nrw.de/de/justiz/gericht?ang=grundbuch&plzort=Aschersleben',
  },
];

const GRUNDBUCH_DOCUMENT_KEYS = new Set([
  'standarddoc-grundbuchauszug',
  'standarddoc-grundbucheintragung',
]);

const BAUAMT_DOCUMENT_KEYS = new Set([
  'standarddoc-bauunterlagen',
  // Gleiche Empfängerfelder: die Anschrift der unteren Bauaufsicht aus dem Bauamt-Import.
  'standarddoc-verfahrensfreiheit-st',
  'standarddoc-verfahrensfreiheit-by',
  'standarddoc-verfahrensfreiheit-bw',
  'standarddoc-verfahrensfreiheit-ni',
  'standarddoc-verfahrensfreiheit-nrw',
  'standarddoc-verfahrensfreiheit-sl',
  'standarddoc-verfahrensfreiheit-sh',
  'standarddoc-verfahrensfreiheit-th',
]);

const PLACEHOLDER_UMLAUT_ALIASES = {
  Empfaenger: 'Empfänger',
  'Empfaenger Strasse': 'Empfänger Straße',
  'Empfaenger PLZ Ort': 'Empfänger PLZ Ort',
  'Strasse, Hausnummer': 'Straße, Hausnummer',
  Flurstueck: 'Flurstück',
};

const STANDARD_DOCUMENT_UMLAUT_REPLACEMENTS = [
  ['{{Empfaenger}}', '{{Empfänger}}'],
  ['{{Empfaenger Strasse}}', '{{Empfänger Straße}}'],
  ['{{Empfaenger PLZ Ort}}', '{{Empfänger PLZ Ort}}'],
  ['{{Strasse, Hausnummer}}', '{{Straße, Hausnummer}}'],
  ['{{Flurstueck}}', '{{Flurstück}}'],
  ['fuer', 'für'],
  ['Fuer', 'Für'],
  ['uebersenden', 'übersenden'],
  ['Uebersenden', 'Übersenden'],
  ['uebermitteln', 'übermitteln'],
  ['Uebermitteln', 'Übermitteln'],
  ['ueber', 'über'],
  ['Ueber', 'Über'],
  ['Pruefung', 'Prüfung'],
  ['Rueckmeldung', 'Rückmeldung'],
  ['Rueckfragen', 'Rückfragen'],
  ['Gebaeude', 'Gebäude'],
  ['Gebaeudedaten', 'Gebäudedaten'],
  ['Gebaeudekonstruktion', 'Gebäudekonstruktion'],
  ['moeglichen', 'möglichen'],
  ['benoetigt', 'benötigt'],
  ['Eigentuemer', 'Eigentümer'],
  ['Eigentuemerseite', 'Eigentümerseite'],
  ['Flurstueck', 'Flurstück'],
  ['Projektgrundstueck', 'Projektgrundstück'],
  ['bestaetigen', 'bestätigen'],
  ['Positionsplaene', 'Positionspläne'],
  ['Stuetzen', 'Stützen'],
  ['Traegern', 'Trägern'],
  ['Pruefberichte', 'Prüfberichte'],
  ['Nachtraege', 'Nachträge'],
  ['Ergaenzende', 'Ergänzende'],
  ['Bestandsplaene', 'Bestandspläne'],
  ['Moeglichkeit', 'Möglichkeit'],
  ['koennen', 'können'],
  ['Unterstuetzung', 'Unterstützung'],
  ['Gruessen', 'Grüßen'],
  ['laeuft ueber', 'läuft über'],
  ['zuruecksetzen', 'zurücksetzen'],
  ['auswaehlen', 'auswählen'],
  ['geoeffnet', 'geöffnet'],
  ['gefuellt', 'gefüllt'],
  ['verfuegbar', 'verfügbar'],
  ['veraendert', 'verändert'],
];

function normalizeStandardDocumentUmlauts(html) {
  if (typeof html !== 'string' || !html) return html;
  return STANDARD_DOCUMENT_UMLAUT_REPLACEMENTS.reduce(
    (current, [from, to]) => current.split(from).join(to),
    html
  );
}

function withUmlautMappingAliases(fields = {}) {
  const next = fields && typeof fields === 'object' ? { ...fields } : {};
  Object.entries(PLACEHOLDER_UMLAUT_ALIASES).forEach(([legacyKey, currentKey]) => {
    if (
      Object.prototype.hasOwnProperty.call(next, legacyKey)
      && !Object.prototype.hasOwnProperty.call(next, currentKey)
    ) {
      next[currentKey] = next[legacyKey];
    }
  });
  return next;
}

function normalizeOfficeSearchText(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/ß/g, 'ss')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

function filterLocalGrundbuchamtDirectory(query) {
  const normalizedQuery = normalizeOfficeSearchText(query);
  if (!normalizedQuery) return [];
  const terms = normalizedQuery.split(/\s+/).filter(Boolean);
  return GRUNDBUCHAMT_DIRECTORY
    .filter((office) => {
      const haystack = normalizeOfficeSearchText([
        office.label,
        office.placeLabel,
        office.recipient,
        office.postalCity,
        office.street,
        ...(office.searchTerms || []),
      ].join(' '));
      return terms.every((term) => haystack.includes(term));
    })
    .slice(0, 5);
}

function getPlaceholderCandidates(key) {
  const currentKey = PLACEHOLDER_UMLAUT_ALIASES[key];
  const legacyKeys = Object.entries(PLACEHOLDER_UMLAUT_ALIASES)
    .filter(([, value]) => value === key)
    .map(([legacy]) => legacy);
  return Array.from(new Set([key, currentKey, ...legacyKeys].filter(Boolean)));
}

function mergeMappingValues(current, values, placeholderList = []) {
  const next = { ...(current && typeof current === 'object' ? current : {}) };
  const placeholderSet = new Set(Array.isArray(placeholderList) ? placeholderList : []);

  Object.entries(values || {}).forEach(([key, rawValue]) => {
    const value = String(rawValue || '').trim();
    if (!value) return;
    const candidates = getPlaceholderCandidates(key);
    const target = candidates.find((candidate) => placeholderSet.has(candidate)) || candidates[0];
    if (!placeholderSet.size || placeholderSet.has(target)) {
      next[target] = value;
    }
  });

  return withUmlautMappingAliases(next);
}

function buildGrundbuchamtMapping(office) {
  if (!office) return {};
  return {
    'Ort Grundbuchamt': office.placeLabel,
    'Adresse des Grundbuchamts': office.addressLines.join('<br>'),
    Empfänger: office.recipient,
    'Empfänger Straße': office.street,
    'Empfänger PLZ Ort': office.postalCity,
    Anrede: 'Sehr geehrte Damen und Herren',
    'Grundbuchamt E-Mail': office.email,
    'Grundbuchamt Telefon': office.phone,
    'Grundbuchamt Fax': office.fax,
  };
}

function deriveOfficeSalutation(lead) {
  const text = String(lead || '').trim();
  if (/^herr\s+/i.test(text)) return `Sehr geehrter ${text}`;
  if (/^frau\s+/i.test(text)) return `Sehr geehrte ${text}`;
  if (text) return `Sehr geehrte Damen und Herren`;
  return 'Sehr geehrte Damen und Herren';
}
// Trennzeichen, mit denen Ämter ihre Kontaktangaben zusammenschreiben. Aus einer
// Webseite kopiert steht die Anschrift oft komplett in einer Zeile:
//   "Kirchgasse 1 + 3 , 36205 Sontra / Telefon 05653 9777-25 / E-Mail: ..."
// Nur der von Leerraum umgebene Schrägstrich trennt - "Karl-Marx-Str. 5/7" ist
// eine Hausnummer und darf nicht auseinanderfallen.
const OFFICE_SEGMENT_SPLIT = /\r?\n|\s+[/|·•]+\s*/;

function officeFieldKey(rohesLabel) {
  const label = String(rohesLabel || '').trim().toLowerCase().replace(/[.:]+$/, '');
  if (/^(leitung|ansprechpartner(?:\/in)?|kontakt|bearbeitung|sachbearbeiter(?:\/in)?)$/.test(label)) return 'lead';
  if (/^(telefon|tel|fon|ruf|rufnummer)$/.test(label)) return 'phone';
  if (/^(fax|telefax)$/.test(label)) return 'fax';
  if (/^(e-?mail|mail)$/.test(label)) return 'email';
  return '';
}

function parseOfficeFieldLine(line) {
  const text = String(line || '').trim();
  if (!text) return null;

  // Mit Doppelpunkt: "Telefon: 05653 9777-25"
  const mitDoppelpunkt = text.match(/^([^:]+):\s*(.+)$/);
  if (mitDoppelpunkt) {
    const schluessel = officeFieldKey(mitDoppelpunkt[1]);
    if (schluessel && mitDoppelpunkt[2].trim()) return [schluessel, mitDoppelpunkt[2].trim()];
  }

  // Ohne Doppelpunkt: "Telefon 05653 9777-25". officeFieldKey entscheidet, ob es
  // wirklich eine Kontaktangabe ist - "Kirchgasse 1 + 3" faellt hier durch.
  const ohneDoppelpunkt = text.match(/^([A-Za-zÄÖÜäöüß.\-/]+)\s+(.+)$/);
  if (ohneDoppelpunkt) {
    const schluessel = officeFieldKey(ohneDoppelpunkt[1]);
    if (schluessel && ohneDoppelpunkt[2].trim()) return [schluessel, ohneDoppelpunkt[2].trim()];
  }

  // Blanke Mailadresse ohne Beschriftung
  if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(text)) return ['email', text];

  return null;
}

/**
 * Trennt "Kirchgasse 1 + 3 , 36205 Sontra" in Straße und PLZ/Ort.
 * Die Postleitzahl muss dafür nicht am Zeilenanfang stehen: In kopierten
 * Anschriften steht sie fast immer mitten im Text.
 */
function splitStreetAndCity(text) {
  const treffer = String(text || '').match(/^\s*(.*?)\s*[,;]?\s*\b(\d{5})\s+(\D[^,;]*?)\s*$/);
  if (!treffer) return null;
  return {
    street: treffer[1].replace(/[,;]\s*$/, '').trim(),
    postalCity: `${treffer[2]} ${treffer[3].trim()}`.trim(),
  };
}

const STREET_HINT = /stra(ss|ß)e|str\.|weg|platz|allee|ring|gasse|damm|ufer|chaussee|markt|feld/i;

function parseBauamtBulkText(value) {
  const segmente = String(value || '')
    .split(OFFICE_SEGMENT_SPLIT)
    .map((teil) => teil.trim())
    .filter(Boolean);

  const parsed = {
    lead: '',
    phone: '',
    fax: '',
    email: '',
    recipient: '',
    street: '',
    postalCity: '',
    salutation: 'Sehr geehrte Damen und Herren',
  };

  const anschrift = [];
  segmente.forEach((segment) => {
    const feld = parseOfficeFieldLine(segment);
    if (feld) {
      // Erster Treffer gewinnt: Steht eine Durchwahl hinter der Hauptnummer,
      // ist die Hauptnummer gemeint.
      if (!parsed[feld[0]]) parsed[feld[0]] = feld[1];
      return;
    }
    anschrift.push(segment);
  });

  const uebrig = [];
  anschrift.forEach((zeile) => {
    const zerlegt = parsed.postalCity ? null : splitStreetAndCity(zeile);
    if (!zerlegt) {
      uebrig.push(zeile);
      return;
    }
    parsed.postalCity = zerlegt.postalCity;
    if (zerlegt.street) parsed.street = zerlegt.street;
  });

  // Steht die Straße in einer eigenen Zeile, ist sie hier noch übrig.
  if (!parsed.street) {
    const index = uebrig.findIndex((zeile) => /\d/.test(zeile) && STREET_HINT.test(zeile));
    if (index >= 0) parsed.street = uebrig.splice(index, 1)[0];
  }

  const ortsname = parsed.postalCity.replace(/^\d{5}\s+/, '').trim();
  parsed.recipient = uebrig.join(', ') || (ortsname ? `Bauamt ${ortsname}` : 'Bauamt');
  parsed.salutation = deriveOfficeSalutation(parsed.lead);

  return parsed;
}

function buildBauamtMapping(parsed) {
  if (!parsed) return {};
  return {
    Empfänger: parsed.recipient,
    'Empfänger Straße': parsed.street,
    'Empfänger PLZ Ort': parsed.postalCity,
    Anrede: parsed.salutation,
    'Bauamt Leitung': parsed.lead,
    'Bauamt Telefon': parsed.phone,
    'Bauamt Fax': parsed.fax,
    'Bauamt E-Mail': parsed.email,
  };
}

function buildDocumentStateKey(documentKey) {
  return documentKey;
}

function decodeHtmlEntities(value) {
  if (typeof value !== 'string') return '';
  return value
    .replace(/&#(\d+);/g, (_m, code) => String.fromCharCode(Number(code)))
    .replace(/&#x([0-9a-fA-F]+);/g, (_m, code) => String.fromCharCode(parseInt(code, 16)))
    .replace(/&auml;/g, 'ä').replace(/&ouml;/g, 'ö').replace(/&uuml;/g, 'ü')
    .replace(/&Auml;/g, 'Ä').replace(/&Ouml;/g, 'Ö').replace(/&Uuml;/g, 'Ü')
    .replace(/&szlig;/g, 'ß').replace(/&amp;/g, '&').replace(/&nbsp;/g, ' ');
}

function extractPlaceholders(html) {
  if (typeof html !== 'string') return [];
  const matches = html.match(/\{\{[^}]+\}\}/g) || [];
  const decoded = matches.map((match) => decodeHtmlEntities(match.slice(2, -2).trim()));
  return Array.from(new Set(decoded)).sort((a, b) => a.localeCompare(b, 'de'));
}

function injectBaseHref(html, baseHref) {
  if (typeof html !== 'string' || !html) return html;
  if (/<base\s/i.test(html)) return html;
  const baseTag = `<base href="${baseHref}">`;
  if (/<head([^>]*)>/i.test(html)) return html.replace(/<head([^>]*)>/i, `<head$1>${baseTag}`);
  return html;
}

function hasStoredDocumentContent(state) {
  return Boolean(state && (state.content || state.editorState));
}

function applyDocumentEditorStateToTemplate(baseHtml, incomingEditorState) {
  if (typeof baseHtml !== 'string' || !baseHtml) return baseHtml;
  if (!incomingEditorState || typeof incomingEditorState !== 'object') return baseHtml;

  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(baseHtml, 'text/html');
    const source = doc.getElementById('contract-source');
    let stateScript = doc.getElementById('enpower-editor-state');

    if (source && typeof incomingEditorState.sourceHtml === 'string') {
      source.innerHTML = incomingEditorState.sourceHtml;
    }
    if (!stateScript) {
      stateScript = doc.createElement('script');
      stateScript.id = 'enpower-editor-state';
      stateScript.type = 'application/json';
      (doc.body || doc.documentElement).appendChild(stateScript);
    }
    stateScript.textContent = JSON.stringify(incomingEditorState, null, 2).replace(/</g, '\\u003c');
    return '<!doctype html>\n' + doc.documentElement.outerHTML;
  } catch (error) {
    console.error('applyDocumentEditorStateToTemplate failed:', error);
    return baseHtml;
  }
}

function materializeStandardDocumentTemplate(originalHtml, state) {
  const editorState = state?.editorState && typeof state.editorState === 'object' ? state.editorState : null;
  if (editorState?.sourceHtml || editorState) {
    return {
      html: applyDocumentEditorStateToTemplate(originalHtml, editorState),
      source: editorState?.sourceHtml ? 'sourceHtml' : 'editorState',
    };
  }
  if (state?.content) {
    return { html: state.content, source: 'content' };
  }
  return { html: null, source: 'original' };
}

function disableEditorInHtml(html) {
  if (typeof html !== 'string' || !html) return html;
  const block = '<style id="enpower-automation-readonly">'
    + '.enpower-editor-panel { display: none !important; }'
    + 'body.editor-mode [contenteditable="true"], body.editor-move-mode [contenteditable="true"] { -webkit-user-modify: read-only; }'
    + '</style>'
    + '<script>(function(){function disable(){var p=document.querySelector(".enpower-editor-panel");if(p)p.remove();document.querySelectorAll("[contenteditable]").forEach(function(n){n.setAttribute("contenteditable","false");});document.body&&document.body.classList.remove("editor-mode","editor-move-mode");}if(document.readyState==="loading"){document.addEventListener("DOMContentLoaded",function(){setTimeout(disable,30);});}else{setTimeout(disable,30);}setTimeout(disable,400);})();</script>';
  if (/<\/head>/i.test(html)) return html.replace(/<\/head>/i, `${block}</head>`);
  if (/<head([^>]*)>/i.test(html)) return html.replace(/<head([^>]*)>/i, `<head$1>${block}`);
  return block + html;
}

function removeCustomerEditorChrome(doc) {
  if (!doc) return;
  doc.querySelectorAll('style#enpower-automation-readonly, style#enpower-editor-style').forEach((node) => node.remove());
  doc.querySelectorAll('.enpower-editor-panel, [data-html-export-ignore], [data-editor-handle]').forEach((node) => node.remove());
  Array.from(doc.querySelectorAll('script')).forEach((script) => {
    const src = script.getAttribute('src') || '';
    const text = script.textContent || '';
    const isEditor = /contract-editor\.js/i.test(src)
      || /ENPOWER_REQUEST_EXPORT|PASSUS_LIBRARY_KEY|enpower-editor-panel|STORAGE_PREFIX\s*=\s*['"]enpower-contract-editor:/i.test(text);
    const isPagination = /contract-pagination\.js/i.test(src)
      || /__ENPOWER_REPAGINATE|function\s+paginateContract|__ENPOWER_PAGINATED/i.test(text);
    const isReadonlyGuard = /enpower-automation-readonly|function\s+disable\(\)\{var p=document\.querySelector\("\.enpower-editor-panel"\)/i.test(text);
    if (isEditor || isPagination || isReadonlyGuard) script.remove();
  });
}

function normalizeScriptSrc(src) {
  return String(src || '').replace(/^\.\//, '').replace(/^\//, '');
}

function ensureScript(doc, src) {
  if (!doc || !src) return;
  const wanted = normalizeScriptSrc(src);
  const exists = Array.from(doc.querySelectorAll('script[src]')).some((script) => (
    normalizeScriptSrc(script.getAttribute('src')) === wanted
  ));
  if (exists) return;
  const script = doc.createElement('script');
  script.src = src;
  (doc.body || doc.documentElement).appendChild(script);
}

function prepareCustomerEditableHtml(html, { documentKey = '' } = {}) {
  if (typeof html !== 'string' || !html.trim()) {
    throw new Error('Die HTML-Datei ist leer.');
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const source = doc.getElementById('contract-source');
  if (!source) {
    throw new Error('Diese HTML-Datei enthält keine Dokumentstruktur (#contract-source).');
  }

  removeCustomerEditorChrome(doc);
  if (doc.body) {
    doc.body.dataset.editorScope = 'standard-document';
    doc.body.dataset.contractKey = documentKey || 'standard-document';
    doc.body.dataset.variantKey = 'ci';
    doc.body.classList.remove('editor-mode', 'editor-move-mode');
  }
  doc.querySelectorAll('[contenteditable]').forEach((node) => node.removeAttribute('contenteditable'));
  doc.querySelectorAll('[draggable]').forEach((node) => node.removeAttribute('draggable'));
  doc.querySelectorAll('.editor-selected,.editor-multi-selected,.editor-dragging,.editor-drop-before,.editor-drop-after').forEach((node) => {
    node.classList.remove('editor-selected', 'editor-multi-selected', 'editor-dragging', 'editor-drop-before', 'editor-drop-after');
  });
  ensureScript(doc, PAGINATION_SCRIPT);
  ensureScript(doc, EDITOR_SCRIPT);
  return '<!doctype html>\n' + doc.documentElement.outerHTML;
}

function cleanCustomerEditableHtmlForExport(doc) {
  if (!doc || !doc.documentElement) {
    throw new Error('Editor-Inhalt konnte nicht gelesen werden.');
  }

  const exportedDoc = doc.documentElement.cloneNode(true);
  removeCustomerEditorChrome({ querySelectorAll: exportedDoc.querySelectorAll.bind(exportedDoc) });
  exportedDoc.querySelectorAll('[contenteditable]').forEach((node) => node.removeAttribute('contenteditable'));
  exportedDoc.querySelectorAll('[draggable]').forEach((node) => node.removeAttribute('draggable'));
  exportedDoc.querySelectorAll('.editor-selected,.editor-multi-selected,.editor-dragging,.editor-drop-before,.editor-drop-after').forEach((node) => {
    node.classList.remove('editor-selected', 'editor-multi-selected', 'editor-dragging', 'editor-drop-before', 'editor-drop-after');
  });
  const body = exportedDoc.querySelector('body');
  if (body) {
    body.classList.remove('editor-mode', 'editor-move-mode');
    delete body.dataset.editorScope;
    delete body.dataset.contractKey;
    delete body.dataset.variantKey;
  }
  const existingPagination = Array.from(exportedDoc.querySelectorAll('script[src]')).some((script) => (
    /contract-pagination\.js/i.test(script.getAttribute('src') || '')
  ));
  if (!existingPagination) {
    const script = doc.createElement('script');
    script.src = PAGINATION_SCRIPT;
    (exportedDoc.querySelector('body') || exportedDoc).appendChild(script);
  }
  return '<!doctype html>\n' + exportedDoc.outerHTML;
}

function safeDownloadName(value, fallback = 'Standarddokument.html') {
  return String(value || fallback)
    .replace(/[\\/:*?"<>|]+/g, '_')
    .replace(/\s+/g, '_')
    .replace(/^_+|_+$/g, '') || fallback;
}

function stripHtmlForDisplay(value) {
  return decodeHtmlEntities(String(value || '')
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim());
}

function looksLikeFieldReference(value) {
  const text = String(value || '').trim();
  if (!text || /\s/.test(text)) return false;
  if (/^(DEAL|CONTACT|COMPANY)\./i.test(text)) return true;
  if (/^UF_CRM_\d+(?:_[A-Z0-9]+)?$/i.test(text)) return true;
  return /^[A-Z0-9_]+$/.test(text) && text.includes('_');
}

function firstLiteralMappingValue(fields, keys) {
  const source = fields && typeof fields === 'object' ? fields : {};
  for (const key of keys) {
    const value = stripHtmlForDisplay(source[key]);
    if (value && !looksLikeFieldReference(value) && !/\{\{[^}]+\}\}/.test(value)) {
      return value;
    }
  }
  return '';
}

function firstMailAddress(values) {
  for (const value of values) {
    const text = String(value || '').trim();
    if (/@/.test(text) && !looksLikeFieldReference(text)) {
      return text;
    }
  }
  return '';
}

function mailTextToHtml(value) {
  return String(value || '')
    .split(/\n{2,}/)
    .map((paragraph) => `<p style="margin:0 0 12px">${escapeHtml(paragraph).replace(/\n/g, '<br>')}</p>`)
    .join('');
}

const DEFAULT_STANDARD_DOCUMENT_SIGNER = 'Randy Wesley Kirsten';
const DEFAULT_STANDARD_DOCUMENT_ORGANIZATION = 'Kirsten und Klein GbR | EN:POWER';

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function sanitizeCertificateValue(value, fallback = '-') {
  const text = decodeHtmlEntities(String(value ?? '').replace(/\s+/g, ' ').trim());
  if (!text || /\{\{[^}]+\}\}/.test(text)) return fallback;
  return text;
}

function formatSigningTimestamp(date) {
  const safeDate = date instanceof Date && !Number.isNaN(date.getTime()) ? date : new Date();
  const offsetHours = -safeDate.getTimezoneOffset() / 60;
  const offsetPrefix = offsetHours >= 0 ? '+' : '-';
  const offset = `${offsetPrefix}${String(Math.abs(offsetHours)).padStart(2, '0')}:00`;
  return `${safeDate.toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })} ${safeDate.toLocaleTimeString('de-DE', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })} (UTC ${offset})`;
}

function fallbackFingerprint(value) {
  let h1 = 0x811c9dc5;
  let h2 = 0x01000193;
  const text = String(value || '');
  for (let index = 0; index < text.length; index += 1) {
    const code = text.charCodeAt(index);
    h1 ^= code;
    h1 = Math.imul(h1, 0x01000193) >>> 0;
    h2 ^= code + index;
    h2 = Math.imul(h2, 0x85ebca6b) >>> 0;
  }
  return `${h1.toString(16).padStart(8, '0')}${h2.toString(16).padStart(8, '0')}`.toUpperCase();
}

async function generateDocumentHash(value) {
  try {
    if (!window.crypto?.subtle || typeof TextEncoder === 'undefined') {
      return fallbackFingerprint(value);
    }
    const data = new TextEncoder().encode(String(value || ''));
    const digest = await window.crypto.subtle.digest('SHA-256', data);
    return Array.from(new Uint8Array(digest))
      .map((byte) => byte.toString(16).padStart(2, '0'))
      .join('')
      .toUpperCase();
  } catch (_error) {
    return fallbackFingerprint(value);
  }
}

function normalizeStandardSignatureSize(value) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 18;
  return Math.max(12, Math.min(42, Math.round(parsed)));
}

function normalizeStandardSignatureAlign(value) {
  return ['left', 'center', 'right'].includes(value) ? value : 'left';
}

function normalizeStandardSignaturePage(value) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 1;
  return Math.max(1, Math.round(parsed));
}

function normalizeStandardSignaturePercent(value, fallback) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return fallback;
  return Math.max(0, Math.min(100, Math.round(parsed * 10) / 10));
}

function getStandardSignatureConfig(source) {
  const overlay = source?.querySelector?.('[data-standard-signature-overlay-marker="true"]') || null;
  if (overlay) {
    return {
      mode: 'overlay',
      marker: overlay,
      anchor: null,
      signer: sanitizeCertificateValue(overlay.getAttribute('data-standard-signature-signer'), ''),
      size: normalizeStandardSignatureSize(overlay.getAttribute('data-standard-signature-size') || 18),
      align: normalizeStandardSignatureAlign(overlay.getAttribute('data-standard-signature-align') || 'left'),
      page: normalizeStandardSignaturePage(overlay.getAttribute('data-standard-signature-page') || 1),
      x: normalizeStandardSignaturePercent(overlay.getAttribute('data-standard-signature-x'), 54),
      y: normalizeStandardSignaturePercent(overlay.getAttribute('data-standard-signature-y'), 82),
    };
  }
  const anchor = source?.querySelector?.('[data-standard-signature-anchor="true"]') || null;
  const fallback = source?.querySelector?.('.signature') || null;
  const target = anchor || fallback;
  return {
    mode: 'flow',
    anchor: target,
    signer: sanitizeCertificateValue(anchor?.getAttribute('data-standard-signature-signer'), ''),
    size: normalizeStandardSignatureSize(anchor?.getAttribute('data-standard-signature-size') || 18),
    align: normalizeStandardSignatureAlign(anchor?.getAttribute('data-standard-signature-align') || 'left'),
  };
}

function applySignatureLineLayout(signatureLine, { size = 18, align = 'left' } = {}) {
  if (!signatureLine) return;
  const normalizedSize = normalizeStandardSignatureSize(size);
  const normalizedAlign = normalizeStandardSignatureAlign(align);
  signatureLine.style.fontSize = `${normalizedSize}pt`;
  signatureLine.style.textAlign = normalizedAlign;
  signatureLine.style.fontFamily = '"Segoe Script", "Brush Script MT", cursive';
  signatureLine.style.color = 'var(--navy)';
  signatureLine.style.borderTopColor = 'var(--orange)';
  signatureLine.style.minHeight = '15mm';
  signatureLine.style.paddingTop = '2.8mm';
  if (normalizedAlign === 'center') {
    signatureLine.style.marginLeft = 'auto';
    signatureLine.style.marginRight = 'auto';
  } else if (normalizedAlign === 'right') {
    signatureLine.style.marginLeft = 'auto';
    signatureLine.style.marginRight = '0';
  } else {
    signatureLine.style.marginLeft = '0';
    signatureLine.style.marginRight = 'auto';
  }
}

function removeStandardDocumentSignature(doc) {
  if (!doc) return;
  doc.querySelectorAll('[data-standard-document-signature="generated"]').forEach((node) => node.remove());
  doc.querySelectorAll('[data-standard-signature-overlay="true"]').forEach((node) => node.remove());
  doc.querySelectorAll('style#standard-document-signature-style').forEach((node) => node.remove());
  doc.querySelectorAll('[data-standard-document-signature-line="true"]').forEach((line) => {
    const originalText = line.getAttribute('data-standard-document-original-text');
    if (originalText !== null) line.textContent = originalText;
    line.classList.remove('standard-document-signed-name');
    line.removeAttribute('data-standard-document-signature-line');
    line.removeAttribute('data-standard-document-original-text');
    line.style.removeProperty('font-size');
    line.style.removeProperty('text-align');
    line.style.removeProperty('font-family');
    line.style.removeProperty('color');
    line.style.removeProperty('border-top-color');
    line.style.removeProperty('min-height');
    line.style.removeProperty('padding-top');
    line.style.removeProperty('margin-left');
    line.style.removeProperty('margin-right');
  });
}

function ensureStandardDocumentSignatureStyle(doc) {
  if (!doc || doc.getElementById('standard-document-signature-style')) return;
  const style = doc.createElement('style');
  style.id = 'standard-document-signature-style';
  style.textContent = `
    .standard-signature-overlay {
      position: absolute;
      z-index: 12;
      min-width: 52mm;
      max-width: 92mm;
      pointer-events: none;
      white-space: nowrap;
      color: var(--navy);
      font-family: "Segoe Script", "Brush Script MT", cursive;
      line-height: 1.05;
    }
    .signature-line.standard-document-signed-name {
      min-height: 15mm;
      padding-top: 2.8mm;
      border-top-color: var(--orange);
      color: var(--navy);
      font-family: "Segoe Script", "Brush Script MT", cursive;
      font-size: 18pt;
      line-height: 1.15;
    }
    .standard-document-signature-note {
      margin: 3mm 0 0;
      color: var(--slate-500);
      font-size: 8.2pt;
      line-height: 1.45;
    }
    .standard-document-certificate {
      margin: 0;
      color: var(--slate-700);
    }
    .standard-document-certificate .certificate-badge {
      display: inline-block;
      margin: 0 0 8mm;
      padding: 2mm 4mm;
      border-radius: 999px;
      background: #ECFDF5;
      color: #047857;
      font-family: var(--heading);
      font-size: 7.5pt;
      font-weight: 800;
      letter-spacing: 0.08em;
      text-transform: uppercase;
    }
    .standard-document-certificate h2 {
      margin: 0 0 4mm;
      font-family: var(--heading);
      font-size: 21pt;
      line-height: 1.08;
      color: var(--navy);
      letter-spacing: 0;
    }
    .standard-document-certificate .certificate-lead {
      margin: 0 0 8mm;
      max-width: 130mm;
      color: var(--slate-500);
      font-size: 9.5pt;
      line-height: 1.6;
    }
    .standard-document-certificate .certificate-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 6mm;
      margin: 0 0 8mm;
    }
    .standard-document-certificate .certificate-card {
      min-height: 28mm;
      padding: 4mm;
      border: 1px solid var(--slate-200);
      border-radius: 8px;
      background: #F8FAFC;
    }
    .standard-document-certificate .certificate-label {
      margin: 0 0 2mm;
      color: var(--slate-500);
      font-family: var(--heading);
      font-size: 7pt;
      font-weight: 800;
      letter-spacing: 0.08em;
      text-transform: uppercase;
    }
    .standard-document-certificate .certificate-value {
      margin: 0;
      color: var(--slate-900);
      font-size: 9pt;
      font-weight: 700;
      line-height: 1.45;
    }
    .standard-document-certificate .certificate-signature {
      margin: 0 0 7mm;
      padding: 5mm 6mm;
      border: 1px solid #A7F3D0;
      border-radius: 10px;
      background: #F0FDF4;
    }
    .standard-document-certificate .certificate-signature-title {
      margin: 0 0 5mm;
      color: #047857;
      font-family: var(--heading);
      font-size: 8pt;
      font-weight: 800;
      letter-spacing: 0.08em;
      text-transform: uppercase;
    }
    .standard-document-certificate .certificate-signature-name {
      margin: 0 0 2mm;
      color: var(--navy);
      font-family: "Segoe Script", "Brush Script MT", cursive;
      font-size: 27pt;
      line-height: 1.05;
    }
    .standard-document-certificate .certificate-signature-meta {
      margin: 0;
      color: var(--slate-500);
      font-size: 8.2pt;
    }
    .standard-document-certificate .certificate-hash {
      margin: 0;
      padding: 3.5mm 4mm;
      border: 1px solid var(--slate-200);
      border-radius: 7px;
      background: #FFFFFF;
      color: var(--slate-900);
      font-family: ui-monospace, SFMono-Regular, Consolas, "Liberation Mono", monospace;
      font-size: 7.8pt;
      line-height: 1.55;
      overflow-wrap: anywhere;
    }
  `;
  (doc.head || doc.documentElement).appendChild(style);
}

function ensureSignatureBlock(source, doc, preferredAnchor = null) {
  let signatureBlock = preferredAnchor || source.querySelector('.signature');
  if (!signatureBlock) {
    signatureBlock = doc.createElement('div');
    signatureBlock.className = 'signature';
    signatureBlock.innerHTML = '<p>Mit freundlichen Grüßen</p><div class="signature-line"></div>';
    source.appendChild(signatureBlock);
  }
  signatureBlock.classList.add('signature');
  let signatureLine = signatureBlock.querySelector('.signature-line');
  if (!signatureLine) {
    signatureLine = doc.createElement('div');
    signatureLine.className = 'signature-line';
    signatureBlock.appendChild(signatureLine);
  }
  return { signatureBlock, signatureLine };
}

async function applyStandardDocumentSignature(html, {
  enabled = true,
  documentKey = '',
  documentLabel = '',
  dealId = '',
  signerName = '',
} = {}) {
  if (typeof html !== 'string' || !html.trim()) return html;

  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const source = doc.getElementById('contract-source');
    if (!source) return html;

    removeStandardDocumentSignature(doc);
    if (!enabled) {
      if (doc.body) doc.body.dataset.standardSignatureDisabled = 'true';
      return '<!doctype html>\n' + doc.documentElement.outerHTML;
    }
    if (doc.body) delete doc.body.dataset.standardSignatureDisabled;

    ensureStandardDocumentSignatureStyle(doc);
    const now = new Date();
    const timestamp = formatSigningTimestamp(now);
    const title = sanitizeCertificateValue(documentLabel || doc.querySelector('title')?.textContent, 'Standarddokument');
    const normalizedDealId = sanitizeCertificateValue(dealId, 'Ohne Deal-ID');
    const signatureConfig = getStandardSignatureConfig(source);
    const configuredSigner = sanitizeCertificateValue(signatureConfig.signer, '');
    const fallbackLine = source.querySelector('.signature-line');
    const roherFallback = String(fallbackLine?.textContent || '').trim();
    // Ein unersetzter Platzhalter ist kein Name. Ohne diese Pruefung wurde
    // "{{Firma / Vertretungsberechtigte Person}}" woertlich als Unterschrift
    // in den Brief geschrieben - genau das stand zuletzt im Dokument.
    const fallbackSigner = /\{\{[^}]*\}\}/.test(roherFallback)
      ? ''
      : sanitizeCertificateValue(roherFallback, '');
    // Rangfolge: der gewaehlte Absender zuerst. Er ist die juengste und
    // ausdrueckliche Angabe, wer unterschreibt.
    //
    // Umgekehrt ging es schief: data-standard-signature-signer wird weiter
    // unten von dieser Funktion selbst ins Dokument geschrieben. Stand dort
    // erst einmal ein Name, gewann er fortan gegen jede Absenderwahl. Die
    // Unterschrift blieb damit bei der Person des ersten Durchlaufs, waehrend
    // der getippte Name darunter laengst der neue Absender war.
    const gewaehlterUnterzeichner = sanitizeCertificateValue(signerName, '');
    const signer = gewaehlterUnterzeichner
      || configuredSigner
      || (fallbackSigner && fallbackSigner !== '-' ? fallbackSigner : '')
      || DEFAULT_STANDARD_DOCUMENT_SIGNER;
    // Ein ausdruecklich bestimmter Name darf eine vorhandene Zeile ersetzen,
    // ein blosser Rueckfall auf die Vorgabe nicht.
    const unterzeichnerBestimmt = Boolean(gewaehlterUnterzeichner || configuredSigner);

    let signatureBlock = null;
    if (signatureConfig.mode === 'overlay' && signatureConfig.marker) {
      signatureConfig.marker.setAttribute('data-standard-signature-signer', signer);
      signatureConfig.marker.setAttribute('data-standard-signature-size', String(signatureConfig.size));
      signatureConfig.marker.setAttribute('data-standard-signature-align', signatureConfig.align);
      signatureConfig.marker.setAttribute('data-standard-signature-page', String(signatureConfig.page));
      signatureConfig.marker.setAttribute('data-standard-signature-x', String(signatureConfig.x));
      signatureConfig.marker.setAttribute('data-standard-signature-y', String(signatureConfig.y));
      signatureConfig.marker.style.display = 'none';
      signatureBlock = fallbackLine?.closest('.signature') || source;
      if (fallbackLine) {
        if (!fallbackLine.getAttribute('data-standard-document-original-text')) {
          fallbackLine.setAttribute('data-standard-document-original-text', fallbackLine.textContent || '');
        }
        if (/\{\{[^}]+\}/.test(fallbackLine.textContent || '') || fallbackLine.textContent?.trim() === signer) {
          fallbackLine.textContent = '';
        }
        fallbackLine.setAttribute('data-standard-document-signature-line', 'true');
      }
    } else {
      const ensured = ensureSignatureBlock(source, doc, signatureConfig.anchor);
      signatureBlock = ensured.signatureBlock;
      const signatureLine = ensured.signatureLine;
      if (!signatureLine.getAttribute('data-standard-document-original-text')) {
        signatureLine.setAttribute('data-standard-document-original-text', signatureLine.textContent || '');
      }
      if (!signatureLine.textContent || /\{\{[^}]+\}\}/.test(signatureLine.textContent) || unterzeichnerBestimmt) {
        signatureLine.textContent = signer;
      }
      signatureLine.classList.add('standard-document-signed-name');
      signatureLine.setAttribute('data-standard-document-signature-line', 'true');
      applySignatureLineLayout(signatureLine, signatureConfig);
    }

    const note = doc.createElement('p');
    note.className = 'standard-document-signature-note';
    note.setAttribute('data-standard-document-signature', 'generated');
    note.textContent = `Elektronisch unterschrieben am ${timestamp}.`;
    signatureBlock.appendChild(note);

    const hashInput = `${documentKey}|${title}|${normalizedDealId}|${source.textContent || ''}|${now.toISOString()}`;
    const documentHash = await generateDocumentHash(hashInput);
    const certificate = doc.createElement('section');
    certificate.className = 'standard-document-certificate';
    certificate.setAttribute('data-editor-page-break-before', '1');
    certificate.setAttribute('data-standard-document-signature', 'generated');
    certificate.innerHTML = `
      <div class="certificate-badge">Zertifikat der Unterzeichnung</div>
      <h2>Digitales Signatur-Zertifikat</h2>
      <p class="certificate-lead">
        Dieses Zertifikat bestätigt, dass das Standarddokument elektronisch signiert wurde.
        Der Fingerabdruck dient zur internen Prüfung der Dokumentintegrität.
      </p>
      <div class="certificate-signature">
        <p class="certificate-signature-title">Elektronisch unterschrieben</p>
        <p class="certificate-signature-name">${escapeHtml(signer)}</p>
        <p class="certificate-signature-meta">Unterzeichnet am ${escapeHtml(timestamp)}</p>
      </div>
      <div class="certificate-grid">
        <div class="certificate-card">
          <p class="certificate-label">Dokument</p>
          <p class="certificate-value">${escapeHtml(title)}</p>
        </div>
        <div class="certificate-card">
          <p class="certificate-label">Deal</p>
          <p class="certificate-value">${escapeHtml(normalizedDealId)}</p>
        </div>
        <div class="certificate-card">
          <p class="certificate-label">Identität</p>
          <p class="certificate-value">${escapeHtml(DEFAULT_STANDARD_DOCUMENT_ORGANIZATION)}</p>
        </div>
        <div class="certificate-card">
          <p class="certificate-label">Status</p>
          <p class="certificate-value">Signatur gesetzt und Fingerabdruck erzeugt</p>
        </div>
      </div>
      <p class="certificate-label">SHA-256 Fingerabdruck</p>
      <p class="certificate-hash">${escapeHtml(documentHash)}</p>
    `;
    source.appendChild(certificate);
    return '<!doctype html>\n' + doc.documentElement.outerHTML;
  } catch (error) {
    console.error('Standarddokument-Signatur konnte nicht angewendet werden:', error);
    return html;
  }
}

export default function StandardDocumentsPage({ apiBase = '/api', session = null }) {
  const accessToken = session?.access_token || null;
  const isAuthenticated = Boolean(accessToken);

  const [activeDocumentKey, setActiveDocumentKey] = useState(STANDARD_DOCUMENTS[0].key);
  const [iframeKey, setIframeKey] = useState(0);
  const [iframeSrcDoc, setIframeSrcDoc] = useState(null);
  const [templateHtml, setTemplateHtml] = useState('');
  const [placeholders, setPlaceholders] = useState([]);
  const [mappingDraft, setMappingDraft] = useState({});
  const [mappingSavedAt, setMappingSavedAt] = useState(null);
  // Werte, die nur fuer den gerade bearbeiteten Vorgang gelten. Sie werden
  // NICHT gespeichert und beim Wechsel der Deal-ID verworfen - sonst schleppte
  // der naechste Kunde die Angaben des vorherigen mit.
  const [vorgangsWerte, setVorgangsWerte] = useState({});
  // Das Feld-Mapping ist im Erstellen-Modus gesperrt. Wer es aendert, aendert
  // es fuer alle kuenftigen Dokumente - das soll eine bewusste Entscheidung
  // sein und nicht beim Ausfuellen eines Anschreibens nebenbei passieren.
  const [mappingEntsperrt, setMappingEntsperrt] = useState(false);
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dealId, setDealId] = useState('');
  const [previewing, setPreviewing] = useState(false);
  const [previewSummary, setPreviewSummary] = useState(null);
  const [standardOfficeLookup, setStandardOfficeLookup] = useState(null);
  // Hinweise der Anfrage Verfahrensfreiheit (genehmigungsanfrage.js): fehlender Datensatz,
  // abweichendes Flurstück, Grenzen des Tatbestands. Rot/gelb über der Vorschau.
  const [genehmigungsanfrage, setGenehmigungsanfrage] = useState(null);
  // Ablage der Anfrage in „Anfrage Baubehörde“ (seit 30.10.2026, genehmigungAblage.js).
  const [ablegenLaeuft, setAblegenLaeuft] = useState(false);
  const [ablageErgebnis, setAblageErgebnis] = useState(null);
  const [unresolved, setUnresolved] = useState([]);
  const [filledHtml, setFilledHtml] = useState(null);
  const [htmlEditMode, setHtmlEditMode] = useState(false);
  const [uploadedHtmlName, setUploadedHtmlName] = useState('');
  const [availableClauses, setAvailableClauses] = useState([]);
  const [selectedClauseIds, setSelectedClauseIds] = useState([]);
  const [iframeHeight, setIframeHeight] = useState(900);
  const [signatureEnabled, setSignatureEnabled] = useState(true);
  const [grundbuchamtQuery, setGrundbuchamtQuery] = useState('');
  const [grundbuchamtResults, setGrundbuchamtResults] = useState([]);
  const [grundbuchamtLoading, setGrundbuchamtLoading] = useState(false);
  const [grundbuchamtError, setGrundbuchamtError] = useState('');
  const [bauamtBulkText, setBauamtBulkText] = useState('');
  const [mailDraft, setMailDraft] = useState({
    from: STANDARD_DOCUMENT_MAIL_SENDERS[0].email,
    to: '',
    cc: '',
    bcc: '',
    subject: '',
    text: '',
    attachPdf: true,
    attachHtml: false,
    syncBitrix: true,
  });
  const [sendingMail, setSendingMail] = useState(false);
  const [faxNumber, setFaxNumber] = useState('');
  const [sendingFax, setSendingFax] = useState(false);
  const [extraAttachments, setExtraAttachments] = useState([]);

  const iframeRef = useRef(null);
  const fileInputRef = useRef(null);
  const attachmentInputRef = useRef(null);
  const lastMailDefaultsRef = useRef({ to: '', subject: '', text: '' });
  // Ob der Benutzer Betreff oder Text wirklich selbst angefasst hat. Vorher
  // wurde das daran erraten, ob der Inhalt noch dem gemerkten Vorgabewert
  // entsprach - und sobald er einmal abwich, blieb er für immer stehen. Genau
  // deshalb stand unter dem Betreff "Grundbuchauszug" noch ein Text über
  // Bauunterlagen. Ein Ref statt State: Tippen soll kein Neuzeichnen auslösen.
  const mailSelbstBearbeitetRef = useRef({ subject: false, text: false });
  const letzteDokumentKeyRef = useRef('');

  const authHeaders = useMemo(() => (
    accessToken ? { Authorization: `Bearer ${accessToken}` } : {}
  ), [accessToken]);

  const activeDocument = useMemo(
    () => STANDARD_DOCUMENTS.find((document) => document.key === activeDocumentKey) || STANDARD_DOCUMENTS[0],
    [activeDocumentKey]
  );

  const stateKey = useMemo(
    () => buildDocumentStateKey(activeDocument.key),
    [activeDocument.key]
  );

  const originalUrl = `${TEMPLATE_BASE_HREF}${activeDocument.file}`;

  const showGrundbuchamtLookup = GRUNDBUCH_DOCUMENT_KEYS.has(activeDocument.key);
  const showBauamtImport = BAUAMT_DOCUMENT_KEYS.has(activeDocument.key);

  const parsedBauamtData = useMemo(() => parseBauamtBulkText(bauamtBulkText), [bauamtBulkText]);

  const documentAddressHint = useMemo(() => (
    firstLiteralMappingValue(mappingDraft, [
      'Adresse des Grundstücks',
      'Adresse des Grundstuecks',
      'Projektadresse',
      'Anschrift des Projektobjektes',
      'Anschrift des Pachtobjektes',
      'Straße, Hausnummer',
      'Strasse, Hausnummer',
      'PLZ, Ort Fläche',
      'PLZ, Ort Flaeche',
      'Postleitzahl, Ort',
    ]) || stripHtmlForDisplay(previewSummary?.dealTitle) || (dealId.trim() ? `Deal ${dealId.trim()}` : '')
  ), [dealId, mappingDraft, previewSummary?.dealTitle]);

  const suggestedMailRecipient = useMemo(() => firstMailAddress([
    standardOfficeLookup?.office?.email,
    parsedBauamtData.email,
    mappingDraft['Grundbuchamt E-Mail'],
    mappingDraft['Bauamt E-Mail'],
    mappingDraft['E-Mail'],
    mappingDraft.Email,
  ]), [mappingDraft, parsedBauamtData.email, standardOfficeLookup?.office?.email]);

  const suggestedFaxNumber = useMemo(() => {
    const candidates = [
      standardOfficeLookup?.office?.fax,
      parsedBauamtData.fax,
      mappingDraft['Grundbuchamt Fax'],
      mappingDraft['Bauamt Fax'],
    ];
    for (const c of candidates) {
      const v = String(c || '').trim();
      if (v) return v;
    }
    return '';
  }, [mappingDraft, parsedBauamtData.fax, standardOfficeLookup?.office?.fax]);

  const defaultMailSubject = useMemo(() => {
    const suffix = documentAddressHint ? ` - ${documentAddressHint}` : '';
    if (activeDocument.key === 'standarddoc-grundbuchauszug') {
      return `Antrag auf Erteilung eines Grundbuchauszugs${suffix}`;
    }
    if (activeDocument.key === 'standarddoc-bauunterlagen') {
      return `Anforderung statischer Berechnungen${suffix}`;
    }
    if (activeDocument.key === 'standarddoc-grundbucheintragung') {
      return `Anschreiben Grundbucheintragung${suffix}`;
    }
    if (istVerfahrensfreiheit(activeDocument.key)) {
      return `Bitte um Bestätigung der Verfahrensfreiheit – Batteriespeicher${suffix}`;
    }
    return `${activeDocument.label}${suffix}`;
  }, [activeDocument.key, activeDocument.label, documentAddressHint]);

  // Der Begleittext benennt das Anliegen der jeweiligen Dokumentenart. Vorher
  // stand dort ein Satz für alles ("anbei erhalten Sie unser Dokument ..."),
  // der beim Wechsel zwar den Namen tauschte, aber nie sagte, worum es geht.
  const defaultMailText = useMemo(() => {
    const projekt = documentAddressHint ? ` zum Projekt ${documentAddressHint}` : '';
    const anliegen = istVerfahrensfreiheit(activeDocument.key)
      ? `anbei übersenden wir Ihnen unsere Bitte um Bestätigung der Verfahrensfreiheit für eine Batteriespeicheranlage${projekt}.\n\n`
        + 'Einen Formulierungsvorschlag für Ihre Bestätigung finden Sie als Anlage 1 im Schreiben.'
      : {
      'standarddoc-grundbuchauszug':
        `anbei übersenden wir Ihnen unseren Antrag auf Erteilung eines Grundbuchauszugs${projekt}.\n\n`
        + 'Die Vollmacht des Eigentümers liegt dem Schreiben bei. Wir bitten Sie um Übersendung '
        + 'des Auszugs an die im Anschreiben genannte Adresse bzw. E-Mail-Adresse.',
      'standarddoc-bauunterlagen':
        `anbei übersenden wir Ihnen unsere Anforderung der statischen Berechnungen und Tragwerksnachweise${projekt}.\n\n`
        + 'Die Vollmacht des Eigentümers liegt dem Schreiben bei. Wir bitten Sie um Übersendung '
        + 'der Unterlagen an die im Anschreiben genannte Adresse bzw. E-Mail-Adresse.',
      'standarddoc-grundbucheintragung':
        `anbei übersenden wir Ihnen unser Anschreiben zur Grundbucheintragung${projekt}.\n\n`
        + 'Die erforderlichen Unterlagen liegen dem Schreiben bei.',
    }[activeDocument.key]
      || `anbei erhalten Sie unser Dokument "${activeDocument.label}"${projekt}.`;

    // Grußformel mit der Person, die auch versendet und unterschreibt.
    const absender = absenderName(mailDraft.from);
    return `Sehr geehrte Damen und Herren,\n\n${anliegen}\n\n`
      + 'Für Rückfragen stehen wir Ihnen jederzeit gerne zur Verfügung.\n\n'
      + `Mit freundlichen Grüßen\n${absender}\n${DEFAULT_STANDARD_DOCUMENT_ORGANIZATION}`;
  }, [activeDocument.key, activeDocument.label, documentAddressHint, mailDraft.from]);

  useEffect(() => {
    const nextDefaults = {
      to: suggestedMailRecipient || '',
      subject: defaultMailSubject,
      text: defaultMailText,
    };

    // Beim Wechsel der Dokumentenart zählt die eigene Bearbeitung nicht mehr:
    // Der Text gehörte zum vorigen Dokument und wäre hier schlicht falsch.
    const dokumentGewechselt = letzteDokumentKeyRef.current !== activeDocument.key;
    if (dokumentGewechselt) {
      letzteDokumentKeyRef.current = activeDocument.key;
      mailSelbstBearbeitetRef.current = { subject: false, text: false };
    }

    setMailDraft((current) => {
      const previousDefaults = lastMailDefaultsRef.current || {};
      const uebernehmen = (feld) => dokumentGewechselt
        || !current[feld]
        || !mailSelbstBearbeitetRef.current[feld];
      return {
        ...current,
        // Der Empfänger hängt am Amt, nicht an der Dokumentenart - eine von
        // Hand eingetragene Adresse darf ein Wechsel nicht wegwerfen.
        to: !current.to || current.to === previousDefaults.to ? nextDefaults.to : current.to,
        subject: uebernehmen('subject') ? nextDefaults.subject : current.subject,
        text: uebernehmen('text') ? nextDefaults.text : current.text,
      };
    });

    lastMailDefaultsRef.current = nextDefaults;
  }, [activeDocument.key, defaultMailSubject, defaultMailText, suggestedMailRecipient]);

  useEffect(() => {
    if (suggestedFaxNumber) {
      setFaxNumber((prev) => prev || suggestedFaxNumber);
    }
  }, [suggestedFaxNumber]);

  const resizePreviewIframe = useCallback(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;
    try {
      const doc = iframe.contentDocument;
      if (!doc) return;
      const pages = doc.getElementById('contract-pages');
      const source = doc.getElementById('contract-source');
      const nextHeight = Math.max(
        900,
        Number(doc.documentElement?.scrollHeight || 0),
        Number(doc.body?.scrollHeight || 0),
        Number(pages?.scrollHeight || 0),
        Number(source?.scrollHeight || 0),
      ) + 32;
      setIframeHeight(Math.ceil(nextHeight));
    } catch (_error) {
      setIframeHeight(900);
    }
  }, []);

  const handlePreviewIframeLoad = useCallback(() => {
    [60, 240, 700, 1400].forEach((delay) => {
      window.setTimeout(resizePreviewIframe, delay);
    });
  }, [resizePreviewIframe]);

  useEffect(() => {
    const timers = [120, 520, 1200].map((delay) => window.setTimeout(resizePreviewIframe, delay));
    return () => timers.forEach((timer) => window.clearTimeout(timer));
  }, [iframeKey, iframeSrcDoc, resizePreviewIframe]);

  const loadTemplate = useCallback(async () => {
    setLoadingTemplate(true);
    setIframeSrcDoc(null);
    setIframeKey((value) => value + 1);
    setPreviewSummary(null);
    setStandardOfficeLookup(null);
    setGenehmigungsanfrage(null);
    setUnresolved([]);
    setFilledHtml(null);
    setHtmlEditMode(false);
    setUploadedHtmlName('');

    try {
      const response = await fetch(originalUrl);
      if (!response.ok) throw new Error('Vorlage nicht gefunden.');
      let html = await response.text();
      if (isAuthenticated) {
        try {
          const stateResponse = await axios.get(`${apiBase}/contracts/state`, {
            params: { contract_key: stateKey },
            headers: authHeaders,
          });
          const state = stateResponse.data?.state || null;
          if (hasStoredDocumentContent(state)) {
            const materialized = materializeStandardDocumentTemplate(html, state);
            if (materialized.html) {
              html = materialized.html;
            }
          }
        } catch (stateError) {
          // Bis 09.10.2026 hier still die Originaldatei (wie bei den Verträgen). Wer erstellen
          // darf, erstellt auf der zuletzt gespeicherten Vorlage - sonst gar nicht.
          const grund = String(stateError?.response?.data?.detail || stateError?.message || '').trim();
          throw new Error(`Die gespeicherte Vorlage konnte nicht geladen werden${grund ? ` (${grund})` : ''}. `
            + 'Ohne sie wird nichts erstellt – bitte die Seite neu laden.');
        }
      }
      html = normalizeStandardDocumentUmlauts(html);
      setTemplateHtml(html);
      setPlaceholders(extractPlaceholders(html));
      setIframeSrcDoc(injectBaseHref(disableEditorInHtml(html), TEMPLATE_BASE_HREF));
    } catch (error) {
      console.error(error);
      toast.error(error?.message || 'Standarddokument konnte nicht geladen werden.');
      setTemplateHtml('');
      setPlaceholders([]);
    } finally {
      setLoadingTemplate(false);
    }
  }, [apiBase, authHeaders, isAuthenticated, originalUrl, stateKey]);

  const loadMapping = useCallback(async () => {
    if (!isAuthenticated) {
      setMappingDraft({});
      setMappingSavedAt(null);
      return;
    }
    try {
      const response = await axios.get(STANDARD_DOCUMENTS_URL, {
        params: { op: 'mapping', contract_key: stateKey },
        headers: authHeaders,
      });
      const mapping = response.data?.mapping;
      setMappingDraft(withUmlautMappingAliases(mapping?.fields || {}));
      setMappingSavedAt(mapping?.updatedAt || null);
    } catch (error) {
      console.error(error);
      const detail = error?.response?.data?.detail;
      if (detail) toast.error(`Mapping nicht ladbar: ${detail}`);
      setMappingDraft({});
      setMappingSavedAt(null);
    }
  }, [authHeaders, isAuthenticated, stateKey]);

  useEffect(() => { loadTemplate(); }, [loadTemplate]);
  useEffect(() => { loadMapping(); }, [loadMapping]);

  useEffect(() => {
    setGrundbuchamtQuery('');
    setGrundbuchamtResults([]);
    setGrundbuchamtError('');
    setGrundbuchamtLoading(false);
    setBauamtBulkText('');
  }, [activeDocumentKey]);

  useEffect(() => {
    if (!showGrundbuchamtLookup) {
      setGrundbuchamtResults([]);
      setGrundbuchamtError('');
      setGrundbuchamtLoading(false);
      return undefined;
    }

    const query = grundbuchamtQuery.trim();
    if (query.length < 2) {
      setGrundbuchamtResults([]);
      setGrundbuchamtError('');
      setGrundbuchamtLoading(false);
      return undefined;
    }

    let cancelled = false;
    const timer = window.setTimeout(async () => {
      setGrundbuchamtLoading(true);
      setGrundbuchamtError('');
      try {
        const response = await axios.get(`${apiBase}/automation/status`, {
          params: { resource: 'grundbuchamt-lookup', q: query },
        });
        if (cancelled) return;
        const offices = Array.isArray(response.data?.offices) ? response.data.offices : [];
        setGrundbuchamtResults(offices);
        setGrundbuchamtError(offices.length > 0 ? '' : 'Kein Grundbuchamt beim Justizportal gefunden. Du kannst das Mapping weiterhin manuell ausfüllen.');
      } catch (error) {
        if (cancelled) return;
        console.error('Grundbuchamt-Suche fehlgeschlagen:', error);
        const fallback = filterLocalGrundbuchamtDirectory(query);
        setGrundbuchamtResults(fallback);
        setGrundbuchamtError(fallback.length > 0
          ? 'Justizportal nicht erreichbar, lokaler Fallback wird angezeigt.'
          : 'Justizportal konnte nicht abgefragt werden. Bitte später erneut versuchen oder manuell mappen.');
      } finally {
        if (!cancelled) setGrundbuchamtLoading(false);
      }
    }, 350);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [apiBase, grundbuchamtQuery, showGrundbuchamtLookup]);

  useEffect(() => {
    if (!isAuthenticated) {
      setAvailableClauses([]);
      setSelectedClauseIds([]);
      return undefined;
    }
    let cancelled = false;
    const controller = new AbortController();
    axios.get(`${apiBase}/contracts/state`, {
      params: { resource: 'clauses', contract_type: activeDocumentKey },
      headers: authHeaders,
      signal: controller.signal,
    }).then((response) => {
      if (cancelled) return;
      const loaded = Array.isArray(response.data?.clauses) ? response.data.clauses : [];
      const filtered = loaded.filter((clause) => (
        clause?.is_active !== false
        && Array.isArray(clause.contract_types)
        && clause.contract_types.includes(activeDocumentKey)
      ));
      filtered.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
      setAvailableClauses(filtered);
      setSelectedClauseIds((previous) => previous.filter((id) => filtered.some((clause) => clause.id === id)));
    }).catch((error) => {
      if (cancelled || error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError') return;
      setAvailableClauses([]);
      setSelectedClauseIds([]);
    });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [activeDocumentKey, apiBase, authHeaders, isAuthenticated]);

  const handleMappingChange = useCallback((placeholder, value) => {
    setMappingDraft((current) => ({ ...current, [placeholder]: value }));
  }, []);

  const applyMappingValues = useCallback((values) => {
    setMappingDraft((current) => mergeMappingValues(current, values, placeholders));
  }, [placeholders]);

  const handleApplyGrundbuchamt = useCallback((office) => {
    if (!office) return;
    applyMappingValues(buildGrundbuchamtMapping(office));
    toast.success(`${office.label} übernommen.`);
  }, [applyMappingValues]);

  const handleApplyBauamtBulk = useCallback(() => {
    const parsed = parseBauamtBulkText(bauamtBulkText);
    const values = buildBauamtMapping(parsed);
    const hasUsableValue = ['Empfänger', 'Empfänger Straße', 'Empfänger PLZ Ort', 'Anrede']
      .some((key) => String(values[key] || '').trim());

    if (!hasUsableValue) {
      toast.error('Im Bauamt-Text wurden keine nutzbaren Daten erkannt.');
      return;
    }

    applyMappingValues(values);
    toast.success('Bauamt-Daten übernommen.');
  }, [applyMappingValues, bauamtBulkText]);

  const handleRemoveStaleField = useCallback((placeholder) => {
    setMappingDraft((current) => {
      if (!Object.prototype.hasOwnProperty.call(current, placeholder)) return current;
      const next = { ...current };
      delete next[placeholder];
      return next;
    });
  }, []);

  const staleFieldKeys = useMemo(() => {
    if (!mappingDraft || typeof mappingDraft !== 'object') return [];
    const placeholderSet = new Set(placeholders);
    return Object.keys(mappingDraft)
      .filter((key) => {
        if (placeholderSet.has(key)) return false;
        const aliasKey = PLACEHOLDER_UMLAUT_ALIASES[key];
        if (aliasKey && placeholderSet.has(aliasKey) && mappingDraft[aliasKey] === mappingDraft[key]) {
          return false;
        }
        return true;
      })
      .sort((a, b) => a.localeCompare(b, 'de'));
  }, [mappingDraft, placeholders]);

  const handleSaveMapping = useCallback(async () => {
    if (!isAuthenticated) {
      toast.error('Bitte zuerst einloggen.');
      return;
    }
    setSaving(true);
    try {
      await axios.post(STANDARD_DOCUMENTS_URL, {
        op: 'mapping',
        contract_key: stateKey,
        fields: mappingDraft,
      }, { headers: authHeaders });
      setMappingSavedAt(new Date().toISOString());
      toast.success('Mapping gespeichert.');
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.detail || error?.message || 'Speichern fehlgeschlagen.');
    } finally {
      setSaving(false);
    }
  }, [authHeaders, isAuthenticated, mappingDraft, stateKey]);

  const handleResetPreview = useCallback(() => {
    if (templateHtml) {
      setIframeSrcDoc(injectBaseHref(disableEditorInHtml(templateHtml), TEMPLATE_BASE_HREF));
      setIframeKey((value) => value + 1);
    }
    setPreviewSummary(null);
    setStandardOfficeLookup(null);
    setGenehmigungsanfrage(null);
    setUnresolved([]);
    setFilledHtml(null);
    setHtmlEditMode(false);
    setUploadedHtmlName('');
  }, [templateHtml]);

  const signStandardDocumentHtml = useCallback((html, enabledOverride = signatureEnabled) => (
    applyStandardDocumentSignature(html, {
      enabled: Boolean(enabledOverride),
      documentKey: activeDocument.key,
      documentLabel: activeDocument.label,
      dealId: dealId.trim() || previewSummary?.dealId || '',
      // Unterschrieben wird von der Person, die auch versendet.
      signerName: absenderName(mailDraft.from),
    })
  ), [activeDocument.key, activeDocument.label, dealId, mailDraft.from, previewSummary?.dealId, signatureEnabled]);

  const readCurrentDocumentHtml = useCallback(async (enabledOverride = signatureEnabled) => {
    let html = '';
    if (!htmlEditMode) {
      html = filledHtml || '';
    } else {
      const iframe = iframeRef.current;
      if (!iframe || !iframe.contentDocument) {
        throw new Error('Editor-Vorschau ist nicht verfügbar.');
      }

      const win = iframe.contentWindow;
      if (win && typeof win.__ENPOWER_GET_EDITOR_EXPORT === 'function') {
        win.__ENPOWER_GET_EDITOR_EXPORT();
      }

      html = cleanCustomerEditableHtmlForExport(iframe.contentDocument);
    }

    const signedHtml = await signStandardDocumentHtml(html, enabledOverride);
    setFilledHtml(signedHtml);
    return signedHtml;
  }, [filledHtml, htmlEditMode, signStandardDocumentHtml, signatureEnabled]);

  const openHtmlInCustomerEditor = useCallback((html, name = '') => {
    const editableHtml = prepareCustomerEditableHtml(html, {
      documentKey: activeDocument.key,
    });
    setIframeSrcDoc(injectBaseHref(editableHtml, TEMPLATE_BASE_HREF));
    setIframeKey((value) => value + 1);
    setFilledHtml(html);
    setHtmlEditMode(true);
    setUploadedHtmlName(name);
    setPreviewSummary(null);
    setStandardOfficeLookup(null);
    setGenehmigungsanfrage(null);
    setUnresolved([]);
  }, [activeDocument.key]);

  const handleImportHtmlFile = useCallback(async (event) => {
    const file = event.target.files && event.target.files[0];
    event.target.value = '';
    if (!file) return;
    if (!/\.html?$/i.test(file.name) && file.type && !/html/i.test(file.type)) {
      toast.error('Bitte eine HTML-Datei auswählen.');
      return;
    }

    try {
      const html = await file.text();
      openHtmlInCustomerEditor(html, file.name);
      toast.success('HTML-Datei im Standarddokument-Editor geöffnet.');
    } catch (error) {
      console.error(error);
      toast.error(error?.message || 'HTML-Datei konnte nicht geladen werden.');
    }
  }, [openHtmlInCustomerEditor]);

  const handleEditFilledDocument = useCallback(() => {
    if (!filledHtml) {
      toast.error('Bitte erst eine Vorschau füllen oder eine HTML-Datei laden.');
      return;
    }

    try {
      const bisherigeAnfrage = genehmigungsanfrage;
      openHtmlInCustomerEditor(filledHtml, uploadedHtmlName || '');
      // Dasselbe Schreiben, nur von Hand weiter bearbeitet: Hinweise und Anlagenstand gelten
      // weiter, und „in Anfrage Baubehörde ablegen“ bleibt erreichbar (30.10.2026).
      if (bisherigeAnfrage) setGenehmigungsanfrage(bisherigeAnfrage);
      toast.success('Dokument ist jetzt individuell bearbeitbar.');
    } catch (error) {
      console.error(error);
      toast.error(error?.message || 'Editor konnte nicht gestartet werden.');
    }
  }, [filledHtml, genehmigungsanfrage, openHtmlInCustomerEditor, uploadedHtmlName]);

  const handleDownloadCurrentHtml = useCallback(async () => {
    if (!filledHtml) {
      toast.error('Erst die Vorschau füllen oder eine HTML-Datei laden.');
      return;
    }
    try {
      // Eigenständig, sonst zeigt die Datei vom Rechner nur das Deckblatt (16.10.2026).
      const { html, vollstaendig } = await machVertragsHtmlEigenstaendig(await readCurrentDocumentHtml());
      const safeName = safeDownloadName(`${activeDocument.label}_Deal-${(dealId || 'preview').replace(/[^A-Za-z0-9_-]+/g, '')}.html`);
      const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = safeName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(link.href), 1000);
      if (vollstaendig) toast.success('HTML heruntergeladen.');
      else toast.warning(`HTML heruntergeladen. ${NUR_ONLINE_HINWEIS}`);
    } catch (error) {
      console.error(error);
      toast.error('Download fehlgeschlagen.');
    }
  }, [activeDocument.label, dealId, filledHtml, readCurrentDocumentHtml]);

  const handlePrintCurrentPdf = useCallback(async () => {
    if (!filledHtml) {
      toast.error('Erst die Vorschau füllen oder eine HTML-Datei laden.');
      return;
    }
    const iframe = iframeRef.current;
    if (!iframe || !iframe.contentWindow) {
      toast.error('Vorschau nicht verfügbar.');
      return;
    }
    try {
      if (htmlEditMode) {
        const html = await readCurrentDocumentHtml();
        setIframeSrcDoc(injectBaseHref(disableEditorInHtml(html), TEMPLATE_BASE_HREF));
        setIframeKey((value) => value + 1);
        setHtmlEditMode(false);
        window.setTimeout(() => {
          const printIframe = iframeRef.current;
          if (printIframe?.contentWindow) {
            printIframe.contentWindow.focus();
            printIframe.contentWindow.print();
          }
        }, 550);
        return;
      }
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
    } catch (error) {
      console.error(error);
      toast.error('Drucken fehlgeschlagen.');
    }
  }, [filledHtml, htmlEditMode, readCurrentDocumentHtml]);

  const handleSendCurrentMail = useCallback(async () => {
    if (!isAuthenticated) {
      toast.error('Bitte zuerst einloggen.');
      return;
    }
    if (!filledHtml) {
      toast.error('Erst die Vorschau füllen oder eine HTML-Datei laden.');
      return;
    }
    if (!mailDraft.to.trim()) {
      toast.error('Bitte eine Empfängeradresse eintragen.');
      return;
    }
    if (!mailDraft.subject.trim()) {
      toast.error('Bitte einen Betreff eintragen.');
      return;
    }

    setSendingMail(true);
    try {
      const html = await readCurrentDocumentHtml();
      const normalizedDealId = (dealId.trim() || previewSummary?.dealId || 'preview').replace(/[^A-Za-z0-9_-]+/g, '');
      const filenameBase = safeDownloadName(`${activeDocument.label}_Deal-${normalizedDealId}`.replace(/\.html?$/i, ''), 'Standarddokument');
      const response = await axios.post(`${apiBase}/contracts/usage`, {
        action: 'standard-document-mail-send',
        source: 'Standarddokumente',
        dealId: dealId.trim() || previewSummary?.dealId || '',
        documentLabel: activeDocument.label,
        filenameBase,
        from: mailDraft.from,
        to: mailDraft.to,
        cc: mailDraft.cc,
        bcc: mailDraft.bcc,
        subject: mailDraft.subject,
        text: mailDraft.text,
        html: mailTextToHtml(mailDraft.text),
        documentHtml: html,
        attachPdf: mailDraft.attachPdf,
        attachHtml: mailDraft.attachHtml,
        syncBitrix: mailDraft.syncBitrix,
        attachments: extraAttachments.map(({ filename, contentBase64, contentType }) => ({ filename, contentBase64, contentType })),
      }, { headers: authHeaders });

      const attachmentNames = Array.isArray(response.data?.attachmentNames) ? response.data.attachmentNames : [];
      const warnings = Array.isArray(response.data?.warnings) ? response.data.warnings : [];
      toast.success(`Mail versendet${attachmentNames.length ? ` mit ${attachmentNames.length} Anhang/Anhängen` : ''}.`);
      if (warnings.length) {
        toast.warning(warnings.join(' '));
      }
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.detail || error?.message || 'Mailversand fehlgeschlagen.');
    } finally {
      setSendingMail(false);
    }
  }, [activeDocument.label, apiBase, authHeaders, dealId, extraAttachments, filledHtml, isAuthenticated, mailDraft, previewSummary?.dealId, readCurrentDocumentHtml]);

  const handleAddAttachments = useCallback((event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;
    const MAX_SINGLE = 10 * 1024 * 1024;
    Promise.all(files.map((file) => new Promise((resolve) => {
      if (file.size > MAX_SINGLE) {
        toast.error(`${file.name} ist zu groß (max. 10 MB).`);
        resolve(null);
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result.split(',')[1] || '';
        resolve({ filename: file.name, contentBase64: base64, contentType: file.type || 'application/octet-stream', size: file.size });
      };
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(file);
    }))).then((results) => {
      const valid = results.filter(Boolean);
      if (valid.length) setExtraAttachments((prev) => [...prev, ...valid]);
    });
    event.target.value = '';
  }, []);

  const handleRemoveAttachment = useCallback((index) => {
    setExtraAttachments((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleSendFax = useCallback(async () => {
    if (!isAuthenticated) { toast.error('Bitte zuerst einloggen.'); return; }
    if (!filledHtml) { toast.error('Erst die Vorschau füllen oder eine HTML-Datei laden.'); return; }
    const toNumber = faxNumber.trim();
    if (!toNumber) { toast.error('Bitte eine Faxnummer eintragen.'); return; }
    setSendingFax(true);
    try {
      const html = await readCurrentDocumentHtml();
      const response = await axios.post(`${apiBase}/contracts/usage`, {
        action: 'standard-document-fax-send',
        html,
        faxTo: toNumber,
        documentName: activeDocument.label,
        dealId: dealId.trim() || previewSummary?.dealId || '',
        syncBitrix: true,
        attachments: extraAttachments.map(({ filename, contentBase64, contentType }) => ({ filename, contentBase64, contentType })),
      }, { headers: authHeaders });
      const merged = response.data?.attachmentsMerged || 0;
      const bitrixOk = response.data?.bitrixSynced;
      const parts = [`Fax an ${response.data?.to || toNumber} erfolgreich gesendet`];
      if (merged > 0) parts.push(`${merged} Anhang/Anhänge als ein PDF gemergt`);
      if (bitrixOk) parts.push('im Bitrix-Deal dokumentiert');
      toast.success(parts.join(' — ') + '.');
      const warnings = Array.isArray(response.data?.warnings) ? response.data.warnings : [];
      if (warnings.length) toast.warning(warnings.join(' '));
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.detail || error?.message || 'Fax-Versand fehlgeschlagen.');
    } finally {
      setSendingFax(false);
    }
  }, [activeDocument.label, apiBase, authHeaders, dealId, extraAttachments, faxNumber, filledHtml, isAuthenticated, previewSummary?.dealId, readCurrentDocumentHtml]);

  // Anlagen der Anfrage Verfahrensfreiheit: Stand vom Server (Vorschau), hochzuladende Anlagen
  // belegt durch „Zusätzliche Anlagen“ in derselben Reihenfolge, wie der Server sie zuordnet.
  const genehmigungsAnlagen = useMemo(() => {
    const liste = Array.isArray(genehmigungsanfrage?.anlagen) ? genehmigungsanfrage.anlagen : [];
    let naechste = 0;
    const anlagen = liste.map((anlage) => {
      if (anlage.quelle !== 'hochladen') return anlage;
      const datei = extraAttachments[naechste];
      if (!datei) return anlage;
      naechste += 1;
      return { ...anlage, bereit: true, hinweis: `hochgeladen: ${datei.filename}` };
    });
    const ueberzaehlig = Math.max(0, extraAttachments.length - naechste);
    return { anlagen, ueberzaehlig, vollstaendig: anlagen.length > 0 && anlagen.every((a) => a.bereit) && ueberzaehlig === 0 };
  }, [extraAttachments, genehmigungsanfrage]);

  const handleGenehmigungAblegen = useCallback(async () => {
    if (!isAuthenticated) { toast.error('Bitte zuerst einloggen.'); return; }
    if (!filledHtml) { toast.error('Erst die Vorschau füllen.'); return; }
    const ablageDealId = dealId.trim() || previewSummary?.dealId || '';
    if (!ablageDealId) { toast.error('Bitte eine Bitrix-Deal-ID eingeben.'); return; }
    setAblegenLaeuft(true);
    setAblageErgebnis(null);
    try {
      const html = await readCurrentDocumentHtml();
      const response = await axios.post(`${apiBase}/contracts/usage`, {
        action: 'standard-document-genehmigung-ablegen',
        contract_key: stateKey,
        dealId: ablageDealId,
        html,
        attachments: extraAttachments.map(({ filename, contentBase64, contentType }) => ({ filename, contentBase64, contentType })),
      }, { headers: authHeaders });
      setAblageErgebnis({ ok: true, ...response.data });
      toast.success(`Anfrage mit ${response.data?.seiten || '?'} Seiten in „Anfrage Baubehörde“ abgelegt.`);
    } catch (error) {
      console.error(error);
      const daten = error?.response?.data || {};
      setAblageErgebnis({ ok: false, detail: daten.detail || error?.message || 'Ablegen fehlgeschlagen.', fehlt: daten.fehlt || [] });
      toast.error(daten.detail || error?.message || 'Ablegen fehlgeschlagen.');
    } finally {
      setAblegenLaeuft(false);
    }
  }, [apiBase, authHeaders, dealId, extraAttachments, filledHtml, isAuthenticated, previewSummary?.dealId, readCurrentDocumentHtml, stateKey]);

  const handleSignatureToggle = useCallback(async (event) => {
    const nextEnabled = event.target.checked;
    setSignatureEnabled(nextEnabled);
    if (!filledHtml) return;

    if (htmlEditMode) {
      toast.info(nextEnabled
        ? 'Unterschrift und Zertifikat werden beim nächsten Export ergänzt.'
        : 'Unterschrift und Zertifikat werden beim nächsten Export entfernt.');
      return;
    }

    try {
      const nextHtml = await signStandardDocumentHtml(filledHtml, nextEnabled);
      setFilledHtml(nextHtml);
      setIframeSrcDoc(injectBaseHref(disableEditorInHtml(nextHtml), TEMPLATE_BASE_HREF));
      setIframeKey((value) => value + 1);
    } catch (error) {
      console.error(error);
      toast.error('Signatur-Option konnte nicht angewendet werden.');
    }
  }, [filledHtml, htmlEditMode, signStandardDocumentHtml]);

  const handleRunPreview = useCallback(async () => {
    if (!isAuthenticated) {
      toast.error('Bitte zuerst einloggen.');
      return;
    }
    if (!dealId.trim()) {
      toast.error('Bitte eine Bitrix-Deal-ID eingeben.');
      return;
    }
    if (!templateHtml) {
      toast.error('Vorlage noch nicht geladen.');
      return;
    }
    setStandardOfficeLookup(null);
    setGenehmigungsanfrage(null);
    setAblageErgebnis(null);
    setPreviewing(true);
    try {
      // Werte, die nicht aus Bitrix kommen, sondern aus dem Tool selbst:
      // die Kontaktdaten unseres Bearbeiters und - falls kein Ansprechpartner
      // ermittelt wurde - die allgemeine Anrede. Sie werden hier ueber das
      // Mapping gelegt, aber NICHT in den gespeicherten Mapping-Stand
      // geschrieben: Der Absender kann je Vorgang wechseln, die Vorlage soll
      // davon unberuehrt bleiben.
      const automatischeFelder = {
        ...buildBearbeiterMapping(mailDraft.from),
        // Anrede und Datum setzen sich IMMER durch, auch gegen einen
        // gespeicherten Eintrag. Grund: Im Grundbuchauszug stand als Datum ein
        // fester Text vom Tag der Einrichtung - jedes Anschreiben trug seither
        // dasselbe veraltete Datum. Ein Briefdatum ist immer "heute", dafuer
        // darf es gar keine gespeicherte Angabe geben. Fuer den Einzelfall
        // bleibt die Ueberschreibung unten offen.
        Anrede: STANDARD_ANREDE,
        Datum: heutigesBriefdatum(),
      };

      const response = await axios.post(`${apiBase}/contracts/usage`, {
        action: 'am-contract-preview',
        contract_key: stateKey,
        contract_label: activeDocument.label,
        template_html: templateHtml,
        deal_id: dealId.trim(),
        template_variant: 'ci',
        variant_label: 'CI',
        // Reihenfolge ist die Rangfolge: gespeichertes Mapping, darueber die
        // Automatikwerte, darueber die Eingaben fuer diesen einen Vorgang.
        mapping: {
          fields: {
            ...mappingDraft,
            ...automatischeFelder,
            ...Object.fromEntries(
              Object.entries(vorgangsWerte).filter(([, wert]) => String(wert || '').trim()),
            ),
          },
        },
        clauses: availableClauses.filter((clause) => selectedClauseIds.includes(clause.id)),
      }, { headers: authHeaders });
      const html = response.data?.html;
      if (typeof html !== 'string' || !html) throw new Error('Backend lieferte kein HTML.');
      const signedHtml = await signStandardDocumentHtml(html);
      setFilledHtml(signedHtml);
      setHtmlEditMode(false);
      setUploadedHtmlName('');
      setIframeSrcDoc(injectBaseHref(disableEditorInHtml(signedHtml), TEMPLATE_BASE_HREF));
      setIframeKey((value) => value + 1);
      setPreviewSummary(response.data?.bitrixSummary || null);
      setStandardOfficeLookup(response.data?.standardOfficeLookup || response.data?.generated?.standardOfficeLookup || null);
      setUnresolved(Array.isArray(response.data?.unresolved) ? response.data.unresolved : []);
      setGenehmigungsanfrage(response.data?.genehmigungsanfrage || response.data?.generated?.genehmigungsanfrage || null);
      toast.success('Standarddokument gefüllt.');
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.detail || error?.message || 'Vorschau fehlgeschlagen.');
    } finally {
      setPreviewing(false);
    }
  }, [activeDocument.label, apiBase, authHeaders, availableClauses, dealId, isAuthenticated, mailDraft.from, mappingDraft, selectedClauseIds, signStandardDocumentHtml, stateKey, templateHtml, vorgangsWerte]);

  const selectedClauseCount = selectedClauseIds.length;

  // Nur gefuellte Ueberschreibungen zaehlen - ein leergeraeumtes Feld ist keine.
  const vorgangsWerteAnzahl = Object.values(vorgangsWerte)
    .filter((wert) => String(wert || '').trim()).length;

  return (
    <div className="responsive-workspace responsive-workspace--standard-documents" style={{ flex: 1, width: '100%', minWidth: 0, minHeight: 0, height: '100%', overflow: 'auto', background: '#E8EEF6', display: 'flex', flexDirection: 'column' }}>
      {/* Kopf mit Titel und Erklaerzeile entfiel am 23.09.2026 - die
          Navigation sagt, wo man ist; die Erklaerung las man einmal. */}
      <div className="responsive-control-bar" style={{ padding: '12px 20px', borderBottom: '1px solid #E2E8F0', background: '#F8FAFC', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 300 }}>
          <label style={{ color: '#475569', fontSize: 11, fontWeight: 700 }}>Dokumentart</label>
          <select
            value={activeDocumentKey}
            onChange={(event) => setActiveDocumentKey(event.target.value)}
            style={{ padding: '8px 10px', border: '1px solid #CBD5E1', borderRadius: 8, background: '#FFFFFF', color: '#0F172A', fontSize: 13, fontWeight: 600, minHeight: 36 }}
          >
            {STANDARD_DOCUMENTS.map((document) => (
              <option key={document.key} value={document.key}>{document.label}</option>
            ))}
          </select>
        </div>
        <div style={{ flex: 1, minWidth: 240, color: '#64748B', fontSize: 12, lineHeight: 1.45, alignSelf: 'flex-end', paddingBottom: 9 }}>
          {activeDocument.description}
        </div>
      </div>


      {htmlEditMode ? (
        <div style={{ padding: '6px 20px', background: '#ECFDF5', color: '#065F46', borderBottom: '1px solid #A7F3D0', fontSize: 12, fontWeight: 700 }}>
          Du bearbeitest das Dokument dieses Deals{uploadedHtmlName ? ` (${uploadedHtmlName})` : ''} – die Vorlage bleibt unverändert.
        </div>
      ) : null}

      <div className="responsive-split-workspace" style={{ width: '100%', minHeight: 0, display: 'flex', alignItems: 'flex-start' }}>
        <div className="responsive-preview-pane" style={{ flex: 1, minWidth: 0, background: '#E8EEF6', position: 'relative', minHeight: iframeHeight }}>
          {iframeSrcDoc ? (
            <iframe
              key={iframeKey}
              ref={iframeRef}
              onLoad={handlePreviewIframeLoad}
              scrolling="no"
              title={`${activeDocument.label} - Vorschau`}
              srcDoc={iframeSrcDoc}
              style={{ width: '100%', height: iframeHeight, border: 0, display: 'block' }}
            />
          ) : null}
        </div>

        <aside className="responsive-side-panel" style={{ width: 380, flexShrink: 0, borderLeft: '1px solid #E2E8F0', background: '#FFFFFF', minHeight: 0, alignSelf: 'flex-start' }}>
          <div style={{ padding: '14px 16px', borderBottom: '1px solid #E2E8F0', background: '#F8FAFC' }}>
            <input
              ref={fileInputRef}
              type="file"
              accept=".html,.htm,text/html"
              onChange={handleImportHtmlFile}
              style={{ display: 'none' }}
            />
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <input
                value={dealId}
                onChange={(event) => {
                  setDealId(event.target.value);
                  // Andere Deal-ID heisst anderer Vorgang: Die Eingaben des
                  // vorherigen Kunden duerfen nicht ins naechste Anschreiben
                  // wandern. Das gespeicherte Mapping bleibt unberuehrt.
                  setVorgangsWerte({});
                }}
                placeholder="Bitrix Deal-ID"
                style={{ flex: 1, padding: '8px 10px', border: '1px solid #CBD5E1', borderRadius: 8, fontSize: 13, minHeight: 36 }}
              />
              <button
                type="button"
                onClick={handleRunPreview}
                disabled={previewing || !isAuthenticated || !dealId.trim()}
                title={isAuthenticated ? 'Felder aus Bitrix holen und Vorschau füllen' : 'Bitte erst einloggen'}
                style={{ padding: '8px 12px', border: '1px solid #122A5C', borderRadius: 8, background: previewing ? '#CBD5E1' : '#122A5C', color: '#FFFFFF', fontSize: 12, fontWeight: 700, cursor: previewing || !isAuthenticated || !dealId.trim() ? 'not-allowed' : 'pointer', opacity: previewing || !isAuthenticated || !dealId.trim() ? 0.6 : 1, minHeight: 36, display: 'inline-flex', alignItems: 'center', gap: 4 }}
              >
                <Play size={12} />
                {previewing ? '...' : 'Füllen'}
              </button>
              <button
                type="button"
                onClick={handleResetPreview}
                title="Auf leere Vorlage zurücksetzen"
                style={{ padding: '8px 10px', border: '1px solid #CBD5E1', borderRadius: 8, background: '#FFFFFF', color: '#475569', fontSize: 12, fontWeight: 700, cursor: 'pointer', minHeight: 36 }}
              >
                <RotateCcw size={12} />
              </button>
            </div>

            <div style={{ marginTop: 8, display: 'flex', gap: 6 }}>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                title="Bereits erzeugte HTML-Datei hochladen und individuell bearbeiten"
                style={{ flex: 1, padding: '7px 10px', border: '1px solid #CBD5E1', borderRadius: 8, background: '#FFFFFF', color: '#122A5C', fontSize: 12, fontWeight: 700, cursor: 'pointer', minHeight: 34, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
              >
                <Upload size={13} />
                HTML-Datei laden
              </button>
              {filledHtml && !htmlEditMode ? (
                <button
                  type="button"
                  onClick={handleEditFilledDocument}
                  title="Aktuell gefülltes Dokument individuell bearbeiten"
                  style={{ flex: 1, padding: '7px 10px', border: '1px solid #F28A0D', borderRadius: 8, background: '#FFF7ED', color: '#9A4A00', fontSize: 12, fontWeight: 700, cursor: 'pointer', minHeight: 34, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
                >
                  <Pencil size={13} />
                  Bearbeiten
                </button>
              ) : null}
            </div>

            <label
              style={{ marginTop: 8, padding: '7px 8px', border: '1px solid #E2E8F0', borderRadius: 8, background: '#FFFFFF', color: '#334155', fontSize: 12, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}
              title="Standarddokumente standardmäßig elektronisch unterschreiben und Zertifikatsseite anhängen"
            >
              <input
                type="checkbox"
                checked={signatureEnabled}
                onChange={handleSignatureToggle}
                style={{ margin: 0, width: 14, height: 14, flexShrink: 0 }}
              />
              <span style={{ flex: 1 }}>Unterschrift + Zertifikat</span>
              <span style={{ color: signatureEnabled ? '#047857' : '#64748B', fontSize: 10, fontWeight: 700 }}>
                {signatureEnabled ? 'aktiv' : 'aus'}
              </span>
            </label>

            {previewSummary ? (
              <div style={{ marginTop: 8, padding: '6px 8px', background: '#ECFDF5', border: '1px solid #A7F3D0', borderRadius: 6, color: '#065F46', fontSize: 11, lineHeight: 1.4 }}>
                Deal #{previewSummary.dealId}{previewSummary.dealTitle ? ` - ${previewSummary.dealTitle}` : ''}
                {previewSummary.contactName ? <><br />Kontakt: {previewSummary.contactName}</> : null}
                <br />{previewSummary.availableKeyCount} Felder aus Bitrix geladen.
                {standardOfficeLookup?.found && standardOfficeLookup.office ? (
                  <><br />Grundbuchamt: {standardOfficeLookup.office.label}</>
                ) : null}
                {unresolved.length > 0 ? (
                  <><br /><strong>Nicht gefüllt:</strong> {unresolved.slice(0, 6).join(', ')}{unresolved.length > 6 ? ` ... (+${unresolved.length - 6})` : ''}</>
                ) : null}
              </div>
            ) : null}

            {genehmigungsanfrage ? (
              <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
                {genehmigungsanfrage.datensatzStand ? (
                  <div style={{ color: '#475569', fontSize: 11 }}>
                    Lageplan-Daten vom {new Date(genehmigungsanfrage.datensatzStand).toLocaleString('de-DE')}
                  </div>
                ) : null}
                {(genehmigungsanfrage.hinweise || []).map((hinweis, index) => {
                  const farbe = {
                    fehler: { bg: '#FEF2F2', rand: '#FECACA', text: '#991B1B' },
                    warnung: { bg: '#FFFBEB', rand: '#FDE68A', text: '#92400E' },
                  }[hinweis.art] || { bg: '#F8FAFC', rand: '#E2E8F0', text: '#475569' };
                  return (
                    <div
                      key={`${hinweis.art}-${index}`}
                      style={{ padding: '6px 8px', background: farbe.bg, border: `1px solid ${farbe.rand}`, borderRadius: 6, color: farbe.text, fontSize: 11, lineHeight: 1.4 }}
                    >
                      {hinweis.text}
                    </div>
                  );
                })}
                {genehmigungsAnlagen.anlagen.length > 0 ? (
                  <div style={{ marginTop: 4, padding: '8px', border: '1px solid #E2E8F0', borderRadius: 6, background: '#FFFFFF', display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <div style={{ color: '#475569', fontSize: 11, fontWeight: 700 }}>Anlagen beim Ablegen</div>
                    {genehmigungsAnlagen.anlagen.map((anlage) => (
                      <div key={anlage.nummer} style={{ fontSize: 11, lineHeight: 1.4, color: anlage.bereit ? '#334155' : '#991B1B' }}>
                        <strong>{anlage.bereit ? '✓' : '✗'} Anlage {anlage.nummer}</strong> · {anlage.titel}
                        {anlage.hinweis ? <span style={{ color: anlage.bereit ? '#64748B' : '#991B1B' }}> – {anlage.hinweis}</span> : null}
                      </div>
                    ))}
                    {genehmigungsAnlagen.ueberzaehlig > 0 ? (
                      <div style={{ fontSize: 11, color: '#991B1B' }}>
                        {genehmigungsAnlagen.ueberzaehlig} zusätzliche Datei(en) nennt das Schreiben in keiner Anlage.
                      </div>
                    ) : null}
                  </div>
                ) : null}
                {filledHtml && istVerfahrensfreiheit(activeDocument.key) ? (
                  <button
                    type="button"
                    onClick={handleGenehmigungAblegen}
                    // Offene Felder prüft der Server am Schreiben, wie es hinausgeht – von Hand
                    // im Editor Gefülltes kennt die Liste „Nicht gefüllt“ nicht.
                    disabled={ablegenLaeuft || !genehmigungsAnlagen.vollstaendig}
                    title={!genehmigungsAnlagen.vollstaendig
                      ? 'Erst ablegen, wenn alle Anlagen beiliegen'
                      : 'Schreiben samt Anlagen als PDF in den Deal legen'}
                    style={{ padding: '7px 10px', border: '1px solid #122A5C', borderRadius: 8, background: '#122A5C', color: '#FFFFFF', fontSize: 12, fontWeight: 700, cursor: ablegenLaeuft ? 'wait' : 'pointer', minHeight: 34, opacity: (ablegenLaeuft || !genehmigungsAnlagen.vollstaendig) ? 0.5 : 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
                  >
                    <Archive size={13} />
                    {ablegenLaeuft ? 'Wird abgelegt…' : 'Abschließen: in „Anfrage Baubehörde“ ablegen'}
                  </button>
                ) : null}
                {ablageErgebnis ? (
                  <div style={{ padding: '6px 8px', borderRadius: 6, fontSize: 11, lineHeight: 1.4, background: ablageErgebnis.ok ? '#F0FDF4' : '#FEF2F2', border: `1px solid ${ablageErgebnis.ok ? '#BBF7D0' : '#FECACA'}`, color: ablageErgebnis.ok ? '#166534' : '#991B1B' }}>
                    {ablageErgebnis.ok
                      ? `Abgelegt in Deal ${ablageErgebnis.dealId}: ${ablageErgebnis.dateiname} (${ablageErgebnis.seiten} Seiten).`
                      : ablageErgebnis.detail}
                  </div>
                ) : null}
              </div>
            ) : null}

            {filledHtml ? (
              <div style={{ marginTop: 8, display: 'flex', gap: 6 }}>
                <button
                  type="button"
                  onClick={handleDownloadCurrentHtml}
                  title="Gefülltes Dokument als HTML-Datei herunterladen"
                  style={{ flex: 1, padding: '7px 10px', border: '1px solid #122A5C', borderRadius: 8, background: '#FFFFFF', color: '#122A5C', fontSize: 12, fontWeight: 700, cursor: 'pointer', minHeight: 34, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
                >
                  <Download size={13} />
                  HTML
                </button>
                <button
                  type="button"
                  onClick={handlePrintCurrentPdf}
                  title="Gefülltes Dokument drucken / als PDF speichern"
                  style={{ flex: 1, padding: '7px 10px', border: '1px solid #122A5C', borderRadius: 8, background: '#122A5C', color: '#FFFFFF', fontSize: 12, fontWeight: 700, cursor: 'pointer', minHeight: 34, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
                >
                  <Printer size={13} />
                  PDF / Drucken
                </button>
              </div>
            ) : null}

            <div style={{ marginTop: 10, padding: '9px', border: '1px solid #E2E8F0', borderRadius: 8, background: '#FFFFFF', display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <Paperclip size={13} color="#475569" />
                <span style={{ flex: 1, color: '#475569', fontSize: 11, fontWeight: 700 }}>Zusätzliche Anlagen</span>
                {extraAttachments.length > 0 && (
                  <span style={{ color: '#0F766E', fontSize: 10, fontWeight: 700 }}>{extraAttachments.length} Datei(en)</span>
                )}
              </div>
              <input
                ref={attachmentInputRef}
                type="file"
                multiple
                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx,.csv,.txt"
                onChange={handleAddAttachments}
                style={{ display: 'none' }}
              />
              <button
                type="button"
                onClick={() => attachmentInputRef.current?.click()}
                title="Werden an das Dokument angehängt und mit ihm als ein PDF per Mail oder Fax versendet"
                style={{ padding: '5px 8px', border: '1px dashed #94A3B8', borderRadius: 7, background: '#F8FAFC', color: '#475569', fontSize: 11, fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 5, minHeight: 30 }}
              >
                <Paperclip size={12} />
                Dateien anhängen
              </button>
              {extraAttachments.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  {extraAttachments.map((att, idx) => (
                    <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '3px 6px', background: '#F1F5F9', borderRadius: 5, fontSize: 11, color: '#334155' }}>
                      <Paperclip size={10} color="#64748B" />
                      <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{att.filename}</span>
                      <span style={{ color: '#94A3B8', fontSize: 10, flexShrink: 0 }}>{(att.size / 1024).toFixed(0)} KB</span>
                      <button
                        type="button"
                        onClick={() => handleRemoveAttachment(idx)}
                        style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', display: 'flex', color: '#94A3B8' }}
                        title="Entfernen"
                      >
                        <XIcon size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div style={{ marginTop: 10, padding: '9px', border: '1px solid #E2E8F0', borderRadius: 8, background: '#FFFFFF', display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <Mail size={13} color="#475569" />
                <span style={{ flex: 1, color: '#475569', fontSize: 11, fontWeight: 700 }}>Mailversand</span>
                <span style={{ color: mailDraft.syncBitrix ? '#047857' : '#64748B', fontSize: 10, fontWeight: 700 }}>
                  {mailDraft.syncBitrix ? 'Bitrix Sync' : 'ohne Sync'}
                </span>
              </div>
              <select
                value={mailDraft.from}
                onChange={(event) => setMailDraft((current) => ({ ...current, from: event.target.value }))}
                style={{ width: '100%', padding: '7px 8px', border: '1px solid #CBD5E1', borderRadius: 7, background: '#FFFFFF', color: '#0F172A', fontSize: 12, minHeight: 32, fontWeight: 700 }}
                title="Absenderadresse"
              >
                {STANDARD_DOCUMENT_MAIL_SENDERS.map((sender) => (
                  <option key={sender.email} value={sender.email}>
                    {sender.label} - {sender.email}
                  </option>
                ))}
              </select>
              <input
                value={mailDraft.to}
                onChange={(event) => setMailDraft((current) => ({ ...current, to: event.target.value }))}
                placeholder="Empfänger E-Mail"
                style={{ width: '100%', padding: '7px 8px', border: '1px solid #CBD5E1', borderRadius: 7, fontSize: 12, minHeight: 32 }}
              />
              <input
                value={mailDraft.cc}
                onChange={(event) => setMailDraft((current) => ({ ...current, cc: event.target.value }))}
                placeholder="CC optional"
                style={{ width: '100%', padding: '7px 8px', border: '1px solid #E2E8F0', borderRadius: 7, fontSize: 12, minHeight: 32 }}
              />
              <input
                value={mailDraft.subject}
                onChange={(event) => { mailSelbstBearbeitetRef.current.subject = true; setMailDraft((current) => ({ ...current, subject: event.target.value })); }}
                placeholder="Betreff"
                style={{ width: '100%', padding: '7px 8px', border: '1px solid #CBD5E1', borderRadius: 7, fontSize: 12, minHeight: 32 }}
              />
              <textarea
                value={mailDraft.text}
                onChange={(event) => { mailSelbstBearbeitetRef.current.text = true; setMailDraft((current) => ({ ...current, text: event.target.value })); }}
                rows={5}
                placeholder="Mailtext"
                style={{ width: '100%', padding: '7px 8px', border: '1px solid #CBD5E1', borderRadius: 7, fontSize: 12, lineHeight: 1.45, resize: 'vertical', minHeight: 104 }}
              />
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#334155', fontSize: 11, fontWeight: 700 }}>
                  <input
                    type="checkbox"
                    checked={mailDraft.attachPdf}
                    onChange={(event) => setMailDraft((current) => ({ ...current, attachPdf: event.target.checked }))}
                    style={{ margin: 0, width: 13, height: 13 }}
                  />
                  PDF anhängen
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#334155', fontSize: 11, fontWeight: 700 }}>
                  <input
                    type="checkbox"
                    checked={mailDraft.attachHtml}
                    onChange={(event) => setMailDraft((current) => ({ ...current, attachHtml: event.target.checked }))}
                    style={{ margin: 0, width: 13, height: 13 }}
                  />
                  HTML anhängen
                </label>
                <label style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: 6, color: '#334155', fontSize: 11, fontWeight: 700 }}>
                  <input
                    type="checkbox"
                    checked={mailDraft.syncBitrix}
                    onChange={(event) => setMailDraft((current) => ({ ...current, syncBitrix: event.target.checked }))}
                    style={{ margin: 0, width: 13, height: 13 }}
                  />
                  Versand im Bitrix-Deal dokumentieren
                </label>
              </div>
              <button
                type="button"
                onClick={handleSendCurrentMail}
                disabled={sendingMail || !isAuthenticated || !filledHtml || !mailDraft.to.trim() || !mailDraft.subject.trim()}
                title={filledHtml ? 'Aktuelles Dokument per Mail senden' : 'Erst Vorschau füllen oder HTML laden'}
                style={{ padding: '8px 10px', border: '1px solid #0F766E', borderRadius: 8, background: sendingMail || !isAuthenticated || !filledHtml || !mailDraft.to.trim() || !mailDraft.subject.trim() ? '#CBD5E1' : '#0F766E', color: '#FFFFFF', fontSize: 12, fontWeight: 700, cursor: sendingMail || !isAuthenticated || !filledHtml || !mailDraft.to.trim() || !mailDraft.subject.trim() ? 'not-allowed' : 'pointer', opacity: sendingMail || !isAuthenticated || !filledHtml || !mailDraft.to.trim() || !mailDraft.subject.trim() ? 0.68 : 1, minHeight: 36, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
              >
                <Send size={13} />
                {sendingMail ? 'Mail wird gesendet...' : 'Mail senden'}
              </button>
            </div>

            <div style={{ marginTop: 10, padding: '9px', border: '1px solid #E2E8F0', borderRadius: 8, background: '#FFFFFF', display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <Phone size={13} color="#475569" />
                <span style={{ flex: 1, color: '#475569', fontSize: 11, fontWeight: 700 }}>Faxversand</span>
              </div>
              <input
                value={faxNumber}
                onChange={(event) => setFaxNumber(event.target.value)}
                placeholder="Faxnummer z.B. 030 12345678"
                style={{ width: '100%', padding: '7px 8px', border: '1px solid #CBD5E1', borderRadius: 7, fontSize: 12, minHeight: 32 }}
              />
              <button
                type="button"
                onClick={handleSendFax}
                disabled={sendingFax || !isAuthenticated || !filledHtml || !faxNumber.trim()}
                title={filledHtml ? 'Aktuelles Dokument per Fax senden' : 'Erst Vorschau füllen oder HTML laden'}
                style={{ padding: '8px 10px', border: '1px solid #7C3AED', borderRadius: 8, background: sendingFax || !isAuthenticated || !filledHtml || !faxNumber.trim() ? '#CBD5E1' : '#7C3AED', color: '#FFFFFF', fontSize: 12, fontWeight: 700, cursor: sendingFax || !isAuthenticated || !filledHtml || !faxNumber.trim() ? 'not-allowed' : 'pointer', opacity: sendingFax || !isAuthenticated || !filledHtml || !faxNumber.trim() ? 0.68 : 1, minHeight: 36, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
              >
                <Phone size={13} />
                {sendingFax ? 'Fax wird gesendet...' : 'Fax senden'}
              </button>
            </div>
          </div>

          {(showGrundbuchamtLookup || showBauamtImport) ? (
            <div style={{ padding: '10px 16px', borderBottom: '1px solid #E2E8F0', background: '#FFFFFF' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                <Building2 size={13} color="#475569" />
                <span style={{ color: '#475569', fontSize: 11, fontWeight: 700 }}>Amt / Empfänger</span>
              </div>

              {showGrundbuchamtLookup ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <div style={{ position: 'relative' }}>
                    <Search size={13} color="#64748B" style={{ position: 'absolute', left: 9, top: 10 }} />
                    <input
                      value={grundbuchamtQuery}
                      onChange={(event) => setGrundbuchamtQuery(event.target.value)}
                      placeholder="Grundbuchamt suchen, z.B. Aschersleben"
                      style={{ width: '100%', padding: '8px 10px 8px 28px', border: '1px solid #CBD5E1', borderRadius: 8, fontSize: 12, minHeight: 34 }}
                    />
                  </div>
                  {grundbuchamtQuery.trim() ? (
                    <>
                      {grundbuchamtLoading ? (
                        <div style={{ padding: '8px 9px', border: '1px dashed #CBD5E1', borderRadius: 8, color: '#64748B', fontSize: 11, lineHeight: 1.4 }}>
                          Suche im Justizportal...
                        </div>
                      ) : null}
                      {!grundbuchamtLoading && grundbuchamtResults.length > 0 ? grundbuchamtResults.map((office) => (
                        <button
                          key={office.id}
                          type="button"
                          onClick={() => handleApplyGrundbuchamt(office)}
                          style={{ width: '100%', padding: '8px 9px', border: '1px solid #E2E8F0', borderRadius: 8, background: '#F8FAFC', color: '#0F172A', textAlign: 'left', cursor: 'pointer' }}
                        >
                          <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 700, color: '#122A5C' }}>
                            <MapPin size={12} />
                            {office.label}
                          </span>
                          <span style={{ display: 'block', marginTop: 3, fontSize: 11, lineHeight: 1.4, color: '#475569' }}>
                            {(office.addressLines || []).join(', ')}
                          </span>
                          <span style={{ display: 'block', marginTop: 3, fontSize: 10, color: '#94A3B8' }}>
                            Quelle: {office.sourceLabel || 'Justizportal Orts- und Gerichtsverzeichnis'}
                          </span>
                        </button>
                      )) : null}
                      {!grundbuchamtLoading && grundbuchamtError ? (
                        <div style={{ padding: '8px 9px', border: '1px dashed #CBD5E1', borderRadius: 8, color: '#64748B', fontSize: 11, lineHeight: 1.4 }}>
                          {grundbuchamtError}
                        </div>
                      ) : null}
                    </>
                  ) : null}
                </div>
              ) : null}

              {showBauamtImport ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <textarea
                    value={bauamtBulkText}
                    onChange={(event) => setBauamtBulkText(event.target.value)}
                    placeholder={'Leitung: Herr Wiesel\n\nRudolf-Breitscheid-Straße 3\n39638 Hansestadt Gardelegen\n\nTelefon:+49 3907 716 171\nFax:+49 3907 716 111\nE-Mail:bauamt@gardelegen.de'}
                    rows={7}
                    style={{ width: '100%', padding: '8px 9px', border: '1px solid #CBD5E1', borderRadius: 8, fontSize: 11.5, lineHeight: 1.45, resize: 'vertical', minHeight: 126 }}
                  />
                  {bauamtBulkText.trim() ? (
                    <div style={{ padding: '7px 8px', border: '1px solid #E2E8F0', borderRadius: 8, background: '#F8FAFC', color: '#475569', fontSize: 11, lineHeight: 1.45 }}>
                      Erkannt: {parsedBauamtData.recipient || '-'}
                      {parsedBauamtData.street ? ` · ${parsedBauamtData.street}` : ''}
                      {parsedBauamtData.postalCity ? ` · ${parsedBauamtData.postalCity}` : ''}
                      {parsedBauamtData.lead ? ` · ${parsedBauamtData.lead}` : ''}
                    </div>
                  ) : null}
                  <button
                    type="button"
                    onClick={handleApplyBauamtBulk}
                    disabled={!bauamtBulkText.trim()}
                    style={{ padding: '7px 10px', border: '1px solid #122A5C', borderRadius: 8, background: bauamtBulkText.trim() ? '#122A5C' : '#CBD5E1', color: '#FFFFFF', fontSize: 12, fontWeight: 700, cursor: bauamtBulkText.trim() ? 'pointer' : 'not-allowed', minHeight: 34, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
                  >
                    <ClipboardCheck size={13} />
                    Bauamt-Daten übernehmen
                  </button>
                </div>
              ) : null}
            </div>
          ) : null}

          <div style={{ padding: '10px 16px', borderBottom: '1px solid #E2E8F0', background: '#F8FAFC' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
              <ListChecks size={13} color="#475569" />
              <span style={{ color: '#475569', fontSize: 11, fontWeight: 700 }}>Optionale Bausteine</span>
              {selectedClauseCount ? (
                <span style={{ marginLeft: 'auto', borderRadius: 999, background: '#FFF7ED', color: '#C2410C', padding: '2px 7px', fontSize: 10, fontWeight: 900 }}>
                  {selectedClauseCount}
                </span>
              ) : null}
            </div>
            {availableClauses.length > 0 ? availableClauses.map((clause) => {
              const checked = selectedClauseIds.includes(clause.id);
              return (
                <label key={clause.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 6, cursor: 'pointer' }}>
                  <input type="checkbox" checked={checked} onChange={() => setSelectedClauseIds((previous) => checked ? previous.filter((id) => id !== clause.id) : [...previous, clause.id])} style={{ marginTop: 2, flexShrink: 0 }} />
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#0F172A' }}>{clause.title}</div>
                    <div style={{ fontSize: 10, color: '#64748B' }}>{clause.insert_position === 'before-signatures' ? 'Vor Unterschriften' : clause.insert_position === 'after-preamble' ? 'Nach Präambel' : 'Ende des Dokuments'}</div>
                  </div>
                </label>
              );
            }) : (
              <div style={{ color: '#64748B', fontSize: 12, lineHeight: 1.4 }}>
                Noch keine passenden Bausteine gespeichert. Über den Paragraphen-Bereich kannst du neue Bausteine für Standarddokumente anlegen.
              </div>
            )}
          </div>

          <div style={{ padding: '12px 16px', borderBottom: '1px solid #E2E8F0', display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Link2 size={14} color="#475569" />
              <span style={{ flex: 1, color: '#475569', fontSize: 12, fontWeight: 700 }}>Felder ({placeholders.length})</span>
              <button
                type="button"
                onClick={() => setMappingEntsperrt((wert) => !wert)}
                title={mappingEntsperrt
                  ? 'Feldzuordnung wieder sperren'
                  : 'Feldzuordnung bearbeiten – gilt für alle künftigen Dokumente'}
                style={{
                  padding: '6px 10px', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer',
                  border: `1px solid ${mappingEntsperrt ? '#B45309' : '#CBD5E1'}`,
                  background: mappingEntsperrt ? '#FEF3C7' : '#FFFFFF',
                  color: mappingEntsperrt ? '#92400E' : '#475569',
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                }}
              >
                {mappingEntsperrt ? <Unlock size={11} /> : <Lock size={11} />}
                {mappingEntsperrt ? 'Zuordnung offen' : 'Zuordnung'}
              </button>
              {mappingEntsperrt ? (
                <button
                  type="button"
                  onClick={handleSaveMapping}
                  disabled={saving || !isAuthenticated}
                  title={mappingSavedAt ? `Zuletzt gespeichert: ${new Date(mappingSavedAt).toLocaleString('de-DE')}` : 'Noch nicht gespeichert'}
                  style={{ padding: '6px 10px', border: '1px solid #122A5C', borderRadius: 6, background: saving ? '#CBD5E1' : '#122A5C', color: '#FFFFFF', fontSize: 11, fontWeight: 700, cursor: saving || !isAuthenticated ? 'not-allowed' : 'pointer', opacity: saving || !isAuthenticated ? 0.6 : 1, display: 'inline-flex', alignItems: 'center', gap: 4 }}
                >
                  <Save size={11} />
                  {saving ? '...' : 'Speichern'}
                </button>
              ) : null}
            </div>

            <div style={{ color: '#64748B', fontSize: 11, lineHeight: 1.45 }}>
              {mappingEntsperrt
                ? 'Änderungen an der Zuordnung gelten für ALLE künftigen Dokumente dieser Vorlage.'
                : 'Trage hier Werte nur für diesen Vorgang ein. Sie gelten nicht für die Vorlage und werden beim Wechsel der Deal-ID verworfen.'}
            </div>

            {vorgangsWerteAnzahl > 0 ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#FFF7ED', border: '1px solid #FED7AA', borderRadius: 6, padding: '6px 8px' }}>
                <span style={{ flex: 1, color: '#9A3412', fontSize: 11, fontWeight: 700 }}>
                  {vorgangsWerteAnzahl} Feld(er) nur für diesen Vorgang überschrieben
                </span>
                <button
                  type="button"
                  onClick={() => setVorgangsWerte({})}
                  style={{ padding: '3px 8px', border: '1px solid #FDBA74', borderRadius: 5, background: '#FFFFFF', color: '#9A3412', fontSize: 10.5, fontWeight: 700, cursor: 'pointer' }}
                >
                  zurücksetzen
                </button>
              </div>
            ) : null}
          </div>

          <div style={{ padding: '8px 0' }}>
            {loadingTemplate ? (
              <div style={{ padding: '12px 16px', color: '#64748B', fontSize: 12 }}>Lade Vorlage...</div>
            ) : placeholders.length === 0 ? (
              <div style={{ padding: '12px 16px', color: '#64748B', fontSize: 12 }}>Keine Platzhalter (<code>{'{{...}}'}</code>) in der Vorlage gefunden.</div>
            ) : placeholders.map((placeholder) => {
              const automatikHinweis = AUTOMATISCHE_PLATZHALTER[placeholder];
              const ueberschrieben = String(vorgangsWerte[placeholder] || '').trim();
              const zuordnung = mappingDraft[placeholder] || '';

              return (
                <div key={placeholder} style={{ padding: '7px 16px', display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <label style={{ flex: 1, color: '#0F172A', fontSize: 12, fontWeight: 700, fontFamily: 'monospace', overflowWrap: 'anywhere' }}>
                      {`{{${placeholder}}}`}
                    </label>
                    {ueberschrieben ? (
                      <span style={{ background: '#FFF7ED', color: '#9A3412', fontSize: 9.5, fontWeight: 700, padding: '1px 6px', borderRadius: 999, flexShrink: 0 }}>
                        überschrieben
                      </span>
                    ) : null}
                  </div>

                  {/* Die Feldzuordnung: nur lesbar, solange die Sperre zu ist.
                      Automatikfelder haben gar keine - dort steht, was das Tool
                      selbst einsetzt. */}
                  {automatikHinweis ? (
                    <div style={{ padding: '5px 8px', background: '#F8FAFC', border: '1px dashed #CBD5E1', borderRadius: 6, color: '#64748B', fontSize: 11, lineHeight: 1.4 }}>
                      <strong style={{ color: '#475569' }}>automatisch</strong> – {automatikHinweis}
                      {automatischerWert(placeholder, mailDraft.from) ? (
                        <div style={{ marginTop: 2, color: '#0F172A', fontFamily: 'monospace', fontSize: 11, overflowWrap: 'anywhere' }}>
                          {automatischerWert(placeholder, mailDraft.from)}
                        </div>
                      ) : null}
                    </div>
                  ) : mappingEntsperrt ? (
                    <input
                      list="standard-document-bitrix-field-hints"
                      value={zuordnung}
                      onChange={(event) => handleMappingChange(placeholder, event.target.value)}
                      placeholder="z.B. DEAL.TITLE"
                      style={{ padding: '6px 8px', border: '1px solid #B45309', borderRadius: 6, fontSize: 12, fontFamily: 'monospace', background: '#FFFBEB' }}
                    />
                  ) : (
                    <div
                      title="Zum Ändern oben auf „Zuordnung“ klicken"
                      style={{ padding: '5px 8px', background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 6, color: zuordnung ? '#475569' : '#94A3B8', fontSize: 11, fontFamily: 'monospace', overflowWrap: 'anywhere' }}
                    >
                      {zuordnung || 'keine Zuordnung'}
                    </div>
                  )}

                  {/* Wert nur fuer diesen Vorgang - immer verfuegbar, auch bei
                      Automatikfeldern. */}
                  <input
                    value={vorgangsWerte[placeholder] || ''}
                    onChange={(event) => setVorgangsWerte((vorher) => ({ ...vorher, [placeholder]: event.target.value }))}
                    placeholder="nur für diesen Vorgang …"
                    style={{
                      padding: '6px 8px', borderRadius: 6, fontSize: 12,
                      border: `1px solid ${ueberschrieben ? '#F28A0D' : '#E2E8F0'}`,
                      background: ueberschrieben ? '#FFF7ED' : '#FFFFFF',
                    }}
                  />
                </div>
              );
            })}
            <datalist id="standard-document-bitrix-field-hints">
              {FIELD_HINTS.map((hint) => (<option key={hint} value={hint} />))}
            </datalist>

            {staleFieldKeys.length > 0 ? (
              <div style={{ margin: '12px 0 0', borderTop: '1px dashed #CBD5E1', background: '#FAFAF9' }}>
                <div style={{ padding: '10px 16px 6px', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Archive size={13} color="#92400E" />
                  <span style={{ flex: 1, color: '#92400E', fontSize: 11, fontWeight: 700 }}>
                    Inaktive Felder ({staleFieldKeys.length})
                  </span>
                </div>
                <div style={{ padding: '0 16px 8px', color: '#78716C', fontSize: 10.5, lineHeight: 1.4 }}>
                  Gespeichert, aber nicht mehr in der Vorlage. Sie bleiben, bis du sie entfernst.
                </div>
                {staleFieldKeys.map((key) => (
                  <div key={key} style={{ padding: '4px 16px', display: 'flex', alignItems: 'center', gap: 6, fontSize: 11 }}>
                    <code style={{ flex: 1, fontFamily: 'monospace', color: '#0F172A', fontSize: 11, overflowWrap: 'anywhere' }}>{`{{${key}}}`}</code>
                    <code style={{ flex: 1, fontFamily: 'monospace', color: '#475569', fontSize: 11, overflowWrap: 'anywhere' }}>{mappingDraft[key] || '-'}</code>
                    <button
                      type="button"
                      onClick={() => handleRemoveStaleField(key)}
                      title={`Mapping für "${key}" entfernen`}
                      style={{ width: 22, height: 22, padding: 0, border: '1px solid #E7E5E4', borderRadius: 4, background: '#FFFFFF', color: '#92400E', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
                    >
                      <XIcon size={11} />
                    </button>
                  </div>
                ))}
              </div>
            ) : null}
          </div>

        </aside>
      </div>
    </div>
  );
}
